/**
 * AssignmentDetail Page
 * Detail view for creating and editing assignments
 */

import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Save,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  User,
  Package,
  X,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Copy,
  Search,
  Leaf,
  Loader2,
  ExternalLink,
  FileText,
  CheckCircle,
  Lock,
  Unlock,
  UserX,
  UserCheck,
  Receipt,
  Workflow,
  History,
  AlertTriangle,
  Navigation
} from 'lucide-react'
import {
  format,
  addDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday
} from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { TimeDropdown } from '../../components/TimeDropdown'
import { ActivityLog } from '../../components/ActivityLog'
import { CommentSection } from '../../components/comments'
import { AttachmentSection } from '../../components/assignment'
import { logAssignmentChange, getStatusLabel } from '../../services/activityLog'
import { sendSMS } from '../../services/sms'
// Status System V2
import {
  ASSIGNMENT_STATUSES_V2,
  getAssignmentStatusNameV2,
  getAssignmentStatusBadgeClassesV2,
  CUSTOMER_COMM_STATUSES,
  CUSTOMER_INVOICE_STATUSES,
  TG_WORK_STATUSES,
  TG_INVOICE_STATUSES
} from '../../services/statusSystemV2'
import { calculateAssignmentStatusV2, type AssignmentDataV2 } from '../../services/assignmentStatusCalculatorV2'
import type { AssignmentStatusV2, TgWorkStatus, TgInvoiceStatus } from '../../types/statusTypes'
// New UI components
import { CustomerCard } from '../../components/assignment/CustomerCard'
import { SubcontractorSection } from '../../components/assignment/SubcontractorSection'
import { CentralReportSection } from '../../components/assignment/CentralReportSection'
import type { CustomerCommStatus, CustomerInvoiceStatus, ContactMethod } from '../../types/statusTypes'
import { DateChangeRebookingModal, type DateChangeResult } from '../../components/dialogs/DateChangeRebookingModal'
import { ConfirmPlanningModal, type PlanningConfirmationResult } from '../../components/dialogs/ConfirmPlanningModal'
import type { CancellationResult } from '../../components/dialogs/CustomerCancellationModal'
import { AddSubcontractorModal, type AddSubcontractorResult } from '../../components/dialogs/AddSubcontractorModal'
import { CalendarSlidePanel } from '../../components/assignment/CalendarSlidePanel'
import { AddressSearch } from '../../components/AddressSearch'

interface AssignmentArticleItem {
  articleId: string
  quantity: number
  estimatedHours?: number
  customPrice?: number
  notes?: string
  excludeFromTgReporting?: boolean
  subcontractorPrices?: Record<string, number>
  subcontractorQuantities?: Record<string, number>  // Per-TG quantities
}

interface AssignmentDetailProps {
  assignmentId?: string
  onClose?: () => void
  onSaved?: (id: string) => void
  onDeleted?: () => void
  prefillSubcontractorId?: string
  prefillDate?: string
  isModal?: boolean
}

export function AssignmentDetail(props: AssignmentDetailProps = {}) {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  useAuth()

  const id = props.assignmentId ?? params.id
  const isModal = props.isModal ?? false
  const isNewAssignment = id === 'new'
  const [loading, setLoading] = useState(!isNewAssignment)
  const [saving, setSaving] = useState(false)

  // Navigation helpers - use callbacks when in modal, otherwise navigate
  const goBack = () => {
    if (props.onClose) props.onClose()
    else navigate('/admin/assignments')
  }

  const goToAssignment = (newId: string) => {
    if (props.onSaved) props.onSaved(newId)
    else navigate(`/admin/assignments/${newId}`)
  }

  const afterDelete = () => {
    if (props.onDeleted) props.onDeleted()
    else navigate('/admin/assignments')
  }

  // URL params for prefilling new assignments (from Planning page)
  const prefillSubcontractorId = props.prefillSubcontractorId ?? searchParams.get('subcontractor')
  const prefillDate = props.prefillDate ?? searchParams.get('date')

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    customer_id: '',
    subcontractor_ids: [] as string[],
    status: 'ohanterad',  // V2 default status
    start_date: '',
    start_time: '',
    end_time: '',
    location: '' as string | { address: string; coordinates?: { lat: number; lng: number } },
    // V2 Customer Status Fields
    customer_comm_status: 'ej_kontaktad' as CustomerCommStatus,
    customer_comm_updated_at: null as string | null,
    customer_informed_at: null as string | null,
    customer_informed_method: null as string | null,
    customer_confirmed_at: null as string | null,
    customer_confirmed_method: null as string | null,
    customer_cancelled_at: null as string | null,
    customer_cancelled_reason: null as string | null,
    customer_invoice_status: 'ej_fakturerad' as CustomerInvoiceStatus,
    customer_invoiced_at: null as string | null,
    customer_invoice_number: null as string | null,
    customer_invoice_date: null as string | null
  })

  const [plannedArticles, setPlannedArticles] = useState<AssignmentArticleItem[]>([])
  const [showArticleSelector, setShowArticleSelector] = useState(false)
  const [showStatusSelector, setShowStatusSelector] = useState(false)
  const [articleSearch, setArticleSearch] = useState('')
  
  // Assignment number for display
  const [assignmentNumber, setAssignmentNumber] = useState<number | null>(null)

  // Admin central reported articles
  const [adminReportedArticles, setAdminReportedArticles] = useState<any[]>([])

  // Collapsible section states
  const [basicInfoCollapsed, setBasicInfoCollapsed] = useState(false)
  const [articlesCollapsed, setArticlesCollapsed] = useState(false)
  const [customerCollapsed, setCustomerCollapsed] = useState(false)
  const [subcontractorCollapsed, setSubcontractorCollapsed] = useState(false)
  const [centralReportCollapsed, setCentralReportCollapsed] = useState(true) // Collapsed by default
  const [activityLogCollapsed, setActivityLogCollapsed] = useState(true) // Collapsed by default

  // Delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Past date confirmation dialog
  const [showPastDateConfirm, setShowPastDateConfirm] = useState(false)
  const [pastDateConfirmed, setPastDateConfirmed] = useState(false)

  // Unlock modal state
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [unlockReason, setUnlockReason] = useState('')
  const [unlocking, setUnlocking] = useState(false)

  // Date change rebooking modal state
  const [showRebookingModal, setShowRebookingModal] = useState(false)
  const [pendingDateChange, setPendingDateChange] = useState<string | null>(null)
  // Time change state
  const [pendingTimeChange, setPendingTimeChange] = useState<{
    newStartTime: string
    newEndTime: string
  } | null>(null)
  const [isTimeChangeOnly, setIsTimeChangeOnly] = useState(false)

  // Planning confirmation modal state
  const [showPlanningModal, setShowPlanningModal] = useState(false)

  // Add subcontractor modal state (for confirmed/in-progress assignments)
  const [showAddSubcontractorModal, setShowAddSubcontractorModal] = useState(false)
  const [pendingSubcontractor, setPendingSubcontractor] = useState<{ id: string; name: string; phone: string | null } | null>(null)
  // Track custom TG statuses for newly added subcontractors
  const [customTgStatuses, setCustomTgStatuses] = useState<Record<string, TgWorkStatus>>({})

  // TG quantity warning modal state
  const [showTgQuantityWarning, setShowTgQuantityWarning] = useState(false)
  const [tgQuantityWarningData, setTgQuantityWarningData] = useState<{
    type: 'add' | 'remove'
    subId: string
    subName: string
    customStatus?: TgWorkStatus
  } | null>(null)

  // Track articles where total was auto-updated from per-TG changes
  const [autoUpdatedTotals, setAutoUpdatedTotals] = useState<Set<string>>(new Set())

  // Check if assignment is locked (completed)
  const isLocked = formData.status === 'slutford' && !isNewAssignment

  // Previous assignments modal state
  const [showPreviousAssignmentsModal, setShowPreviousAssignmentsModal] = useState(false)
  const [previousAssignments, setPreviousAssignments] = useState<Array<{
    id: string
    assignment_number: number | null
    title: string
    description: string | null
    start_date: string | null
    status: string
    subcontractors: Array<{ name: string; phone: string | null }>
    articles: Array<{ name: string; quantity: number; unit: string | null }>
  }>>([])
  const [selectedPreviousAssignments, setSelectedPreviousAssignments] = useState<Set<string>>(new Set())
  const [loadingPreviousAssignments, setLoadingPreviousAssignments] = useState(false)

  // Location state
  const [useCustomerAddress, setUseCustomerAddress] = useState(true) // Default to customer address

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    title?: boolean
    customer_id?: boolean
  }>({})

  // Last saved timestamp - reset when navigating to a different assignment
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // Reset lastSaved when id changes (navigating to different assignment)
  useEffect(() => {
    setLastSaved(null)
  }, [id])
  
  // Calendar picker state
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showCalendarPanel, setShowCalendarPanel] = useState(false)

  // Navigation between filtered assignments (with wrap-around)
  const [filteredAssignmentIds, setFilteredAssignmentIds] = useState<string[]>([])
  const currentIndex = id && !isNewAssignment ? filteredAssignmentIds.indexOf(id) : -1
  const canNavigate = filteredAssignmentIds.length > 1 && currentIndex >= 0
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : filteredAssignmentIds.length - 1
  const nextIndex = currentIndex < filteredAssignmentIds.length - 1 ? currentIndex + 1 : 0
  const previousId = canNavigate ? filteredAssignmentIds[previousIndex] : null
  const nextId = canNavigate ? filteredAssignmentIds[nextIndex] : null

  // Load filtered assignment IDs from sessionStorage (skip in modal)
  useEffect(() => {
    if (isModal) return
    const stored = sessionStorage.getItem('filteredAssignmentIds')
    if (stored) {
      try {
        const ids = JSON.parse(stored)
        if (Array.isArray(ids)) {
          setFilteredAssignmentIds(ids)
        }
      } catch (e) {
        console.error('Error parsing filtered assignment IDs:', e)
      }
    }
  }, [isModal])

  // Navigate to previous/next assignment with unsaved changes check (with wrap-around)
  const navigateToPrevious = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        navigate(`/admin/assignments/${previousId}`)
      }
    } else {
      navigate(`/admin/assignments/${previousId}`)
    }
  }

  const navigateToNext = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        navigate(`/admin/assignments/${nextId}`)
      }
    } else {
      navigate(`/admin/assignments/${nextId}`)
    }
  }

  // Keyboard shortcuts: Page Up/Down to navigate between assignments (with wrap-around) - skip in modal
  useEffect(() => {
    if (isModal) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable) {
        return
      }

      if (!canNavigate) return

      let targetId: string | null = null
      if (e.key === 'PageUp') {
        targetId = previousId
      } else if (e.key === 'PageDown') {
        targetId = nextId
      }

      if (targetId) {
        e.preventDefault()
        if (hasUnsavedChanges()) {
          if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
            navigate(`/admin/assignments/${targetId}`)
          }
        } else {
          navigate(`/admin/assignments/${targetId}`)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isModal, canNavigate, previousId, nextId])

  const [datePickerMonth, setDatePickerMonth] = useState<Date>(new Date())

  // Dropdown refs for click-outside handling
  const statusDropdownRef = useRef<HTMLDivElement>(null)
  const datePickerRef = useRef<HTMLDivElement>(null)
  const articleSelectorRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)

  // Sync date picker month when form date changes (e.g., when loading existing assignment)
  useEffect(() => {
    if (formData.start_date) {
      setDatePickerMonth(new Date(formData.start_date))
    }
  }, [formData.start_date])

  // Reset past date confirmation when date changes
  useEffect(() => {
    setPastDateConfirmed(false)
  }, [formData.start_date])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setShowStatusSelector(false)
      }
      if (datePickerRef.current && !datePickerRef.current.contains(target)) {
        setShowDatePicker(false)
      }
      if (articleSelectorRef.current && !articleSelectorRef.current.contains(target)) {
        setShowArticleSelector(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Store original values for change tracking
  const [originalData, setOriginalData] = useState<typeof formData | null>(null)
  const [originalArticles, setOriginalArticles] = useState<AssignmentArticleItem[]>([])

  // Check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!originalData) return false

    // Compare form data
    const formChanged = JSON.stringify(formData) !== JSON.stringify(originalData)

    // Compare articles
    const articlesChanged = JSON.stringify(plannedArticles) !== JSON.stringify(originalArticles)

    return formChanged || articlesChanged
  }

  // Warn before leaving with unsaved changes (skip in modal)
  useEffect(() => {
    if (isModal) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  // Fetch customers
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('active', true)
        .order('full_name')
      if (error) throw error
      return data
    }
  })

  // Fetch subcontractors
  const { data: subcontractors = [] } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return data
    }
  })

  // Fetch articles
  const { data: articles = [] } = useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('name')
      if (error) throw error
      return data
    }
  })

  // Get selected customer's price list prices
  const selectedCustomer = customers.find(c => c.id === formData.customer_id)
  const customerAddress = selectedCustomer
    ? `${selectedCustomer.address_street || ''}, ${selectedCustomer.address_city || ''}`.replace(/^, |, $/g, '')
    : ''
  const { data: customerPrices = {} } = useQuery({
    queryKey: ['customer-prices', selectedCustomer?.price_list_id],
    queryFn: async () => {
      if (!selectedCustomer?.price_list_id) return {}
      const { data, error } = await supabase
        .from('price_list_articles')
        .select('article_id, price_excl_vat')
        .eq('price_list_id', selectedCustomer.price_list_id)
      if (error) throw error
      const priceMap: Record<string, number> = {}
      data?.forEach(p => {
        priceMap[p.article_id] = p.price_excl_vat
      })
      return priceMap
    },
    enabled: !!selectedCustomer?.price_list_id
  })

  // Fetch subcontractor reports for this assignment
  const { data: subcontractorReports = [], refetch: refetchReports } = useQuery({
    queryKey: ['assignment-reports', id],
    queryFn: async () => {
      if (!id || isNewAssignment) return []
      const { data, error } = await supabase
        .from('assignment_subcontractors')
        .select(`
          id,
          assignment_id,
          subcontractor_id,
          tg_status,
          tg_invoice_status,
          report_text,
          reported_at,
          admin_feedback,
          feedback_at,
          reported_articles,
          report_images,
          is_draft,
          draft_saved_at,
          attested_at,
          attested_by,
          attester:user_profiles!attested_by (
            full_name
          ),
          declined_at,
          declined_reason,
          tg_invoice_received_at,
          tg_invoice_number,
          subcontractors (
            id,
            name,
            email,
            phone,
            color
          )
        `)
        .eq('assignment_id', id)
      if (error) throw error
      return data || []
    },
    enabled: !!id && !isNewAssignment
  })

  // Check if any TG has submitted a report (prevents deletion)
  const hasAnyReports = subcontractorReports.some((r: any) => r.reported_at !== null)

  // Fetch all assignments for the same customer
  const { data: customerAssignments = [] } = useQuery({
    queryKey: ['customer-assignments', formData.customer_id, id],
    queryFn: async () => {
      if (!formData.customer_id) return []
      
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          start_date,
          start_time,
          end_time,
          status,
          location,
          customers(id, full_name),
          assignment_subcontractors(
            subcontractor_id,
            subcontractors(id, name, color)
          )
        `)
        .eq('customer_id', formData.customer_id)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })
      
      if (error) throw error
      
      // Filter out the current assignment
      return (data || []).filter(a => a.id !== id) as any[]
    },
    enabled: !!formData.customer_id && !!id && !isNewAssignment
  })

  // Load assignment data
  useEffect(() => {
    if (!id) {
      goBack()
      return
    }

    if (isNewAssignment) {
      // Prefill form from URL params (from Planning page)
      if (prefillDate || prefillSubcontractorId) {
        setFormData(prev => ({
          ...prev,
          start_date: prefillDate || prev.start_date,
          subcontractor_ids: prefillSubcontractorId ? [prefillSubcontractorId] : prev.subcontractor_ids
        }))
      }
      setLoading(false)
      return
    }

    const loadAssignment = async () => {
      try {
        setLoading(true)
        
        // CRITICAL FIX: Verify session exists before querying
        // This prevents queries from running before the session token is available
        // RLS policies require auth context, so queries will hang without a valid session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError || !sessionData?.session) {
          console.error('No session available for assignment query')
          toast.error('Autentisering krävs. Logga in igen.')
          navigate('/login')
          return
        }
        
        // Fetch assignment
        const { data: assignment, error } = await supabase
          .from('assignments')
          .select(`
            *,
            customers(id, full_name, address_street, address_city),
            assignment_subcontractors(subcontractor_id, subcontractors(id, name, color)),
            assignment_articles(id, article_id, quantity, custom_price, notes, exclude_from_tg_reporting, subcontractor_prices, subcontractor_quantities, articles(id, name))
          `)
          .eq('id', id)
          .single()

        if (error) {
          console.error('Error loading assignment:', error)
          toast.error(`Kunde inte ladda uppdrag: ${error.message || 'Okänt fel'}`)
          // Don't navigate away immediately - let user see the error
          return
        }

        if (!assignment) {
          toast.error('Uppdrag hittades inte')
          // Wait a moment before navigating so user can see the error
          setTimeout(() => goBack(), 2000)
          return
        }

        // Set assignment number
        setAssignmentNumber(assignment.assignment_number)

        // Set admin reported articles
        setAdminReportedArticles(assignment.admin_reported_articles || [])

        // Set form data
        const loadedData = {
          title: assignment.title,
          description: assignment.description || '',
          customer_id: assignment.customer_id,
          subcontractor_ids: assignment.assignment_subcontractors?.map((as: any) => as.subcontractor_id) || [],
          status: assignment.status || 'ohanterad',
          start_date: assignment.start_date,
          start_time: assignment.start_time || '',
          end_time: assignment.end_time || '',
          location: assignment.location || '',
          // V2 Customer Status Fields
          customer_comm_status: assignment.customer_comm_status || 'ej_kontaktad',
          customer_comm_updated_at: assignment.customer_comm_updated_at || null,
          customer_informed_at: assignment.customer_informed_at || null,
          customer_informed_method: assignment.customer_informed_method || null,
          customer_confirmed_at: assignment.customer_confirmed_at || null,
          customer_confirmed_method: assignment.customer_confirmed_method || null,
          customer_cancelled_at: assignment.customer_cancelled_at || null,
          customer_cancelled_reason: assignment.customer_cancelled_reason || null,
          customer_invoice_status: assignment.customer_invoice_status || 'ej_fakturerad',
          customer_invoiced_at: assignment.customer_invoiced_at || null,
          customer_invoice_number: assignment.customer_invoice_number || null,
          customer_invoice_date: assignment.customer_invoice_date || null
        }
        setFormData(loadedData)
        setOriginalData(loadedData)

        // Determine if location matches customer address
        if (assignment.customers) {
          const customerAddr = `${assignment.customers.address_street}, ${assignment.customers.address_city}`
          const locationMatches = assignment.location === customerAddr
          setUseCustomerAddress(locationMatches)
        } else {
          setUseCustomerAddress(false)
        }

        // Set planned articles
        if (assignment.assignment_articles) {
          const loadedArticles = assignment.assignment_articles.map((aa: any) => ({
            articleId: aa.article_id,
            quantity: aa.quantity,
            customPrice: aa.custom_price,
            notes: aa.notes,
            excludeFromTgReporting: aa.exclude_from_tg_reporting || false,
            subcontractorPrices: aa.subcontractor_prices || {},
            subcontractorQuantities: aa.subcontractor_quantities || {}
          }))
          setPlannedArticles(loadedArticles)
          setOriginalArticles(loadedArticles)
        }

      } catch (error: any) {
        console.error('Error loading assignment:', error)
        toast.error(`Kunde inte ladda uppdrag: ${error?.message || 'Okänt fel'}`)
        // Wait a moment before navigating so user can see the error
        setTimeout(() => goBack(), 2000)
      } finally {
        setLoading(false)
      }
    }

    loadAssignment()
  }, [id, navigate, isNewAssignment, prefillDate, prefillSubcontractorId])

  // Track previous customer ID to detect changes
  const prevCustomerIdRef = useRef(formData.customer_id)

  // Auto-populate location from customer
  // For new assignments: always set from customer when first selected
  // For existing assignments: only update if useCustomerAddress is true and customer changes
  useEffect(() => {
    const prevCustomerId = prevCustomerIdRef.current
    const currentCustomerId = formData.customer_id

    // Only act on customer changes (not initial load for existing assignments)
    const customerChanged = prevCustomerId !== currentCustomerId

    if (currentCustomerId && customerChanged) {
      const customer = customers.find(c => c.id === currentCustomerId)
      if (customer && customer.address_street) {
        const address = `${customer.address_street}, ${customer.address_city}`
        // Update location if new assignment OR if we're using customer address
        if (isNewAssignment || useCustomerAddress) {
          setFormData(prev => ({ ...prev, location: address }))
          setUseCustomerAddress(true)
        }
      }
    }

    prevCustomerIdRef.current = currentCustomerId
  }, [formData.customer_id, customers, isNewAssignment, useCustomerAddress])

  const handleSave = async () => {
    // Validate required fields (date is optional - status system handles missing dates)
    const errors: typeof validationErrors = {}
    if (!formData.title) errors.title = true
    if (!formData.customer_id) errors.customer_id = true

    // Check if date is in the past for NEW assignments - show confirmation dialog
    if (isNewAssignment && formData.start_date && !pastDateConfirmed) {
      const selectedDate = new Date(formData.start_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      selectedDate.setHours(0, 0, 0, 0)

      if (selectedDate < today) {
        setShowPastDateConfirm(true)
        return
      }
    }

    // Block editing of completed or cancelled assignments
    if (!isNewAssignment && (formData.status === 'slutford' || formData.status === 'avbrutten')) {
      toast.error('Kan inte ändra slutförda eller avbrutna uppdrag')
      return
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      const missingFields = []
      if (errors.title) missingFields.push('Titel')
      if (errors.customer_id) missingFields.push('Kund')
      toast.error(`Fyll i obligatoriska fält: ${missingFields.join(', ')}`)
      return
    }

    // Clear validation errors on successful validation
    setValidationErrors({})

    try {
      setSaving(true)

      // Calculate status based on gardener assignment and planned time (V2)
      const statusInput: AssignmentDataV2 = {
        status: isNewAssignment ? 'ohanterad' : (formData.status as AssignmentStatusV2 || 'ohanterad'),
        start_date: formData.start_date || null,
        start_time: formData.start_time || null,
        subcontractor_ids: formData.subcontractor_ids || [],
        customer_comm_status: 'ej_kontaktad',
        customer_invoice_status: 'ej_fakturerad'
      }
      const calculatedStatus = calculateAssignmentStatusV2(statusInput)

      const assignmentData = {
        title: formData.title,
        description: formData.description || null,
        customer_id: formData.customer_id,
        status: calculatedStatus,
        start_date: formData.start_date || null,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        location: formData.location || null
      }

      if (isNewAssignment) {
        // Create new assignment
        const { data, error } = await supabase
          .from('assignments')
          .insert(assignmentData)
          .select()
          .single()

        if (error) throw error

        // Add subcontractors
        if (formData.subcontractor_ids.length > 0) {
          const subInserts = formData.subcontractor_ids.map(subId => ({
            assignment_id: data.id,
            subcontractor_id: subId,
            tg_status: 'preliminar_tilldelad',
            tg_invoice_status: 'ej_fakturerad'
          }))
          await supabase.from('assignment_subcontractors').insert(subInserts)
        }

        // Add articles
        if (plannedArticles.length > 0) {
          const articleInserts = plannedArticles.map(item => ({
            assignment_id: data.id,
            article_id: item.articleId,
            quantity: item.quantity,
            custom_price: item.customPrice,
            notes: item.notes,
            exclude_from_tg_reporting: item.excludeFromTgReporting || false,
            subcontractor_prices: item.subcontractorPrices || {},
            subcontractor_quantities: item.subcontractorQuantities || {}
          }))
          await supabase.from('assignment_articles').insert(articleInserts)
        }

        // Log creation
        await logAssignmentChange(data.id, 'created', {
          description: `skapade uppdraget "${formData.title}"`
        })

        toast.success('Uppdrag skapat!')
        queryClient.invalidateQueries({ queryKey: ['assignments'] })
        goToAssignment(data.id)

      } else {
        // Update existing assignment
        const { error } = await supabase
          .from('assignments')
          .update({
            ...assignmentData,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)

        if (error) throw error

        // Log changes if we have original data
        if (originalData && id) {
          // Check for status change
          if (originalData.status !== formData.status) {
            await logAssignmentChange(id, 'status_changed', {
              fieldName: 'status',
              oldValue: originalData.status,
              newValue: formData.status,
              description: `ändrade status från "${getStatusLabel(originalData.status)}" till "${getStatusLabel(formData.status)}"`
            })
          }

          // Check for date/time changes
          if (originalData.start_date !== formData.start_date) {
            await logAssignmentChange(id, 'date_changed', {
              fieldName: 'start_date',
              oldValue: originalData.start_date,
              newValue: formData.start_date
            })
          }
          if (originalData.start_time !== formData.start_time || originalData.end_time !== formData.end_time) {
            await logAssignmentChange(id, 'date_changed', {
              fieldName: 'time',
              oldValue: `${originalData.start_time}-${originalData.end_time}`,
              newValue: `${formData.start_time}-${formData.end_time}`
            })
          }

          // Check for customer change
          if (originalData.customer_id !== formData.customer_id) {
            const oldCustomer = customers.find(c => c.id === originalData.customer_id)
            const newCustomer = customers.find(c => c.id === formData.customer_id)
            await logAssignmentChange(id, 'customer_changed', {
              fieldName: 'customer_id',
              oldValue: oldCustomer?.full_name || originalData.customer_id,
              newValue: newCustomer?.full_name || formData.customer_id
            })
          }

          // Check for subcontractor changes
          const oldSubs = originalData.subcontractor_ids.sort().join(',')
          const newSubs = formData.subcontractor_ids.sort().join(',')
          if (oldSubs !== newSubs) {
            const oldNames = originalData.subcontractor_ids
              .map(id => subcontractors.find(s => s.id === id)?.name)
              .filter(Boolean)
              .join(', ') || 'Ingen'
            const newNames = formData.subcontractor_ids
              .map(id => subcontractors.find(s => s.id === id)?.name)
              .filter(Boolean)
              .join(', ') || 'Ingen'
            await logAssignmentChange(id, 'subcontractor_changed', {
              oldValue: oldNames,
              newValue: newNames
            })
          }
        }

        // Update subcontractors - only add new ones and remove deleted ones
        // IMPORTANT: Do NOT delete-and-reinsert as this loses report data!
        const existingSubIds = subcontractorReports.map((r: any) => r.subcontractor_id)
        const newSubIds = formData.subcontractor_ids

        // Find subcontractors to remove (exist in DB but not in form)
        const toRemove = existingSubIds.filter((subId: string) => !newSubIds.includes(subId))

        // Find subcontractors to add (exist in form but not in DB)
        const toAdd = newSubIds.filter(subId => !existingSubIds.includes(subId))

        // Remove deleted subcontractors
        if (toRemove.length > 0) {
          // Get user_ids for the TGs being removed (for notification cleanup)
          const { data: removedTgData } = await supabase
            .from('subcontractors')
            .select('user_id')
            .in('id', toRemove)

          await supabase
            .from('assignment_subcontractors')
            .delete()
            .eq('assignment_id', id)
            .in('subcontractor_id', toRemove)

          // Delete notifications for removed TGs for this assignment
          if (removedTgData && removedTgData.length > 0) {
            const userIds = removedTgData.map(tg => tg.user_id).filter(Boolean)
            if (userIds.length > 0) {
              await supabase
                .from('notifications')
                .delete()
                .in('user_id', userIds)
                .like('link', `%/assignments/${id}%`)
            }
          }
        }

        // Add new subcontractors (use custom status if set, otherwise default)
        if (toAdd.length > 0) {
          const subInserts = toAdd.map(subId => ({
            assignment_id: id,
            subcontractor_id: subId,
            tg_status: customTgStatuses[subId] || 'preliminar_tilldelad',
            tg_invoice_status: 'ej_fakturerad'
          }))
          await supabase.from('assignment_subcontractors').insert(subInserts)

          // Clear custom statuses after save
          setCustomTgStatuses({})
        }

        // Update articles - delete all and re-insert
        await supabase
          .from('assignment_articles')
          .delete()
          .eq('assignment_id', id)

        if (plannedArticles.length > 0) {
          const articleInserts = plannedArticles.map(item => ({
            assignment_id: id,
            article_id: item.articleId,
            quantity: item.quantity,
            custom_price: item.customPrice,
            notes: item.notes,
            exclude_from_tg_reporting: item.excludeFromTgReporting || false,
            subcontractor_prices: item.subcontractorPrices || {},
            subcontractor_quantities: item.subcontractorQuantities || {}
          }))
          await supabase.from('assignment_articles').insert(articleInserts)
        }

        // Update original data after save (including recalculated status)
        const updatedFormData = { ...formData, status: calculatedStatus }
        setFormData(updatedFormData)
        setOriginalData(updatedFormData)
        setOriginalArticles(plannedArticles)
        setLastSaved(new Date())

        toast.success('Uppdrag uppdaterat!')
        queryClient.invalidateQueries({ queryKey: ['assignments'] })
        queryClient.invalidateQueries({ queryKey: ['assignment', id] })
        queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', id] })
        // Refetch subcontractor reports to update UI
        await refetchReports()
      }
    } catch (error) {
      console.error('Error saving assignment:', error)
      toast.error('Kunde inte spara uppdrag')
    } finally {
      setSaving(false)
    }
  }

  // V2 Customer Status Change Handlers
  const handleCustomerCommStatusChange = async (newStatus: CustomerCommStatus, method?: ContactMethod) => {
    if (!id || isNewAssignment) return

    // Don't do anything if status is already the same
    if (formData.customer_comm_status === newStatus) return

    const now = new Date().toISOString()
    const updateData: Record<string, any> = {
      customer_comm_status: newStatus,
      customer_comm_updated_at: now,
      updated_at: now
    }

    // Set/clear timestamps based on status
    // When going backwards, clear the timestamps for later stages
    if (newStatus === 'ej_kontaktad') {
      // Reset all - going back to start
      updateData.customer_informed_at = null
      updateData.customer_informed_method = null
      updateData.customer_confirmed_at = null
      updateData.customer_confirmed_method = null
      updateData.customer_cancelled_at = null
      updateData.customer_cancelled_reason = null
    } else if (newStatus === 'informerad') {
      updateData.customer_informed_at = now
      updateData.customer_informed_method = method || null
      // Clear later stages
      updateData.customer_confirmed_at = null
      updateData.customer_confirmed_method = null
      updateData.customer_cancelled_at = null
      updateData.customer_cancelled_reason = null
    } else if (newStatus === 'bekraftad') {
      updateData.customer_confirmed_at = now
      updateData.customer_confirmed_method = method || null
      // Clear cancelled if going back from avbokad
      updateData.customer_cancelled_at = null
      updateData.customer_cancelled_reason = null
    } else if (newStatus === 'avbokad') {
      updateData.customer_cancelled_at = now
      // Note: Cancel reason should be handled via a dialog - for now we skip it
    }

    try {
      const { error } = await supabase
        .from('assignments')
        .update(updateData)
        .eq('id', id)

      if (error) throw error

      // Update local state (both formData and originalData since this is an immediate save)
      // Must match the updateData logic above for clearing timestamps
      let stateUpdate: Record<string, any> = {
        customer_comm_status: newStatus,
        customer_comm_updated_at: now
      }

      if (newStatus === 'ej_kontaktad') {
        stateUpdate = {
          ...stateUpdate,
          customer_informed_at: null,
          customer_informed_method: null,
          customer_confirmed_at: null,
          customer_confirmed_method: null,
          customer_cancelled_at: null,
          customer_cancelled_reason: null
        }
      } else if (newStatus === 'informerad') {
        stateUpdate = {
          ...stateUpdate,
          customer_informed_at: now,
          customer_informed_method: method || null,
          customer_confirmed_at: null,
          customer_confirmed_method: null,
          customer_cancelled_at: null,
          customer_cancelled_reason: null
        }
      } else if (newStatus === 'bekraftad') {
        stateUpdate = {
          ...stateUpdate,
          customer_confirmed_at: now,
          customer_confirmed_method: method || null,
          customer_cancelled_at: null,
          customer_cancelled_reason: null
        }
      } else if (newStatus === 'avbokad') {
        stateUpdate = {
          ...stateUpdate,
          customer_cancelled_at: now
        }
      }

      setFormData(prev => ({ ...prev, ...stateUpdate }))
      setOriginalData(prev => prev ? { ...prev, ...stateUpdate } : prev)

      // Log the change
      await logAssignmentChange(id, 'customer_comm_status_changed', {
        fieldName: 'customer_comm_status',
        oldValue: formData.customer_comm_status,
        newValue: newStatus,
        description: `Kundstatus ändrad till ${newStatus}${method ? ` (via ${method})` : ''}`
      })

      toast.success('Kundstatus uppdaterad')
      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
    } catch (error) {
      console.error('Error updating customer comm status:', error)
      toast.error('Kunde inte uppdatera kundstatus')
    }
  }

  const handleRegisterCustomerInvoice = async (invoiceNumber: string, invoiceDate: string) => {
    if (!id || isNewAssignment) return

    const now = new Date().toISOString()
    const updateData: Record<string, any> = {
      customer_invoice_status: 'fakturerad',
      customer_invoice_number: invoiceNumber,
      customer_invoice_date: invoiceDate,
      customer_invoiced_at: now,
      updated_at: now
    }

    // Check if assignment should transition to slutford
    // Requirements: customer invoiced + all TG invoices received + status is attesterad
    let shouldCompleteAssignment = false
    if (formData.status === 'attesterad') {
      // Check if all TG invoices are received
      const allTgInvoicesReceived = subcontractorReports?.every(
        r => r.tg_invoice_status === 'faktura_mottagen' || r.tg_status === 'avsagt'
      ) ?? true

      if (allTgInvoicesReceived) {
        shouldCompleteAssignment = true
        updateData.status = 'slutford'
      }
    }

    try {
      const { error } = await supabase
        .from('assignments')
        .update(updateData)
        .eq('id', id)

      if (error) throw error

      // Update local state (both formData and originalData since this is an immediate save)
      const stateUpdate: Record<string, any> = {
        customer_invoice_status: 'fakturerad',
        customer_invoice_number: invoiceNumber,
        customer_invoice_date: invoiceDate,
        customer_invoiced_at: now,
        ...(shouldCompleteAssignment && { status: 'slutford' })
      }
      setFormData(prev => ({ ...prev, ...stateUpdate }))
      setOriginalData(prev => prev ? { ...prev, ...stateUpdate } : prev)

      // Log the change
      await logAssignmentChange(id, 'customer_invoice_status_changed', {
        fieldName: 'customer_invoice_status',
        oldValue: formData.customer_invoice_status,
        newValue: 'fakturerad',
        description: `Kundfaktura registrerad: ${invoiceNumber} (${invoiceDate})`
      })

      if (shouldCompleteAssignment) {
        await logAssignmentChange(id, 'status_changed', {
          fieldName: 'status',
          oldValue: 'attesterad',
          newValue: 'slutford',
          description: 'Uppdraget slutfört automatiskt (kund fakturerad + alla TG-fakturor mottagna)'
        })
        toast.success('Kundfaktura registrerad! Uppdraget är nu slutfört.')
      } else {
        toast.success('Kundfaktura registrerad')
      }

      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
    } catch (error) {
      console.error('Error registering customer invoice:', error)
      toast.error('Kunde inte registrera kundfaktura')
    }
  }

  // Check if date change requires rebooking modal
  const requiresRebookingModal = (newDate: string): boolean => {
    // Only show modal for assignments with status 'planerad' or later
    const rebookingStatuses = ['planerad', 'pagaende', 'granskning', 'attesterad']
    if (!rebookingStatuses.includes(formData.status)) return false

    // Only if date actually changed
    if (newDate === originalData?.start_date) return false

    // Must have an original date to be a "change"
    if (!originalData?.start_date) return false

    return true
  }

  // Handle date change - check if rebooking modal is needed
  const handleDateChange = (newDate: string) => {
    if (requiresRebookingModal(newDate)) {
      setPendingDateChange(newDate)
      setIsTimeChangeOnly(false)
      setPendingTimeChange(null)
      setShowRebookingModal(true)
    } else {
      // Direct change without modal
      setFormData(prev => ({ ...prev, start_date: newDate }))
    }
  }

  // Check if time change requires rebooking modal
  const requiresTimeChangeModal = (newStartTime: string, newEndTime: string): boolean => {
    // Only show modal for assignments with status 'planerad' or later
    const rebookingStatuses = ['planerad', 'pagaende', 'granskning', 'attesterad']
    if (!rebookingStatuses.includes(formData.status)) return false

    // Must have original times to be a "change"
    if (!originalData?.start_time && !originalData?.end_time) return false

    // Check if either start or end time changed
    const startTimeChanged = newStartTime !== originalData?.start_time
    const endTimeChanged = newEndTime !== originalData?.end_time

    if (!startTimeChanged && !endTimeChanged) return false

    return true
  }

  // Handle time change - check if rebooking modal is needed
  const handleTimeChange = (newStartTime: string, newEndTime: string) => {
    if (requiresTimeChangeModal(newStartTime, newEndTime)) {
      setPendingTimeChange({ newStartTime, newEndTime })
      setIsTimeChangeOnly(true)
      setPendingDateChange(formData.start_date) // Keep same date
      setShowRebookingModal(true)
    } else {
      // Direct change without modal
      setFormData(prev => ({ ...prev, start_time: newStartTime, end_time: newEndTime }))
    }
  }

  // Wrapper for start time changes
  const handleStartTimeChange = (newStartTime: string) => {
    handleTimeChange(newStartTime, formData.end_time)
  }

  // Wrapper for end time changes
  const handleEndTimeChange = (newEndTime: string) => {
    handleTimeChange(formData.start_time, newEndTime)
  }

  // Handle rebooking modal confirmation
  const handleRebookingConfirm = async (result: DateChangeResult) => {
    if (!id) return
    // For date changes, need pendingDateChange; for time-only changes, need pendingTimeChange
    if (!isTimeChangeOnly && !pendingDateChange) return
    if (isTimeChangeOnly && !pendingTimeChange) return

    const now = new Date().toISOString()
    const updates: Record<string, any> = {
      updated_at: now
    }

    // Apply date or time changes
    if (isTimeChangeOnly && pendingTimeChange) {
      updates.start_time = pendingTimeChange.newStartTime
      updates.end_time = pendingTimeChange.newEndTime
    } else if (pendingDateChange) {
      updates.start_date = pendingDateChange
    }

    // Handle customer status
    if (result.customerAction === 'reset_to_ej_kontaktad') {
      updates.customer_comm_status = 'ej_kontaktad'
      updates.customer_comm_updated_at = now
      updates.customer_informed_at = null
      updates.customer_informed_method = null
      updates.customer_confirmed_at = null
      updates.customer_confirmed_method = null
    } else if (result.customerAction === 'set_informerad') {
      updates.customer_comm_status = 'informerad'
      updates.customer_comm_updated_at = now
      updates.customer_informed_at = now
    }

    // If resetting to preliminar, also update assignment status
    if (result.resetToPreliminar) {
      updates.status = 'preliminar'
    }

    // If reverting pågående to planerad (moving to future date)
    if (result.revertToPlanned) {
      updates.status = 'planerad'
    }

    try {
      // Update assignment
      const { error: assignmentError } = await supabase
        .from('assignments')
        .update(updates)
        .eq('id', id)

      if (assignmentError) throw assignmentError

      // Handle TG changes based on resetToPreliminar
      if (result.resetToPreliminar) {
        // Reset all TGs to preliminar_tilldelad
        await supabase
          .from('assignment_subcontractors')
          .update({ tg_status: 'preliminar_tilldelad' })
          .eq('assignment_id', id)
          .neq('tg_status', 'avsagt') // Don't reset TGs who declined

        // Send SMS to TGs with phone numbers (per-TG setting)
        const assignmentIdentifier = assignmentNumber ? `#${assignmentNumber} ${formData.title}` : formData.title
        // Format original date/time
        const originalTime = formData.start_time ? formData.start_time.substring(0, 5) : ''
        const originalDateTime = originalTime ? `${originalData?.start_date} ${originalTime}` : originalData?.start_date
        // Use custom message if provided, otherwise default
        const extraInfo = result.preliminarMessage || 'Vi återkopplar när vi vet mera.'
        const smsMessage = `Uppdraget ${assignmentIdentifier} (planerat ${originalDateTime}) behöver planeras om. ${extraInfo}`
        let smsSentCount = 0

        for (const report of subcontractorReports) {
          if (report.tg_status === 'avsagt') continue // Skip declined TGs
          if (!result.sendSmsTo[report.id]) continue // Skip if SMS disabled for this TG
          const tgData = report.subcontractors as any
          if (tgData?.phone) {
            try {
              await sendSMS({
                recipient_type: 'subcontractor',
                recipient_id: report.subcontractor_id,
                recipient_name: tgData.name || 'TG',
                recipient_phone: tgData.phone,
                message: smsMessage,
                assignment_id: id
              })
              smsSentCount++
            } catch (smsError) {
              console.error('Error sending SMS to TG:', smsError)
            }
          }
        }

        if (smsSentCount > 0) {
          toast.success(`SMS skickat till ${smsSentCount} TG${smsSentCount > 1 ? ':s' : ''}`)
        }

        // Log the reset
        await logAssignmentChange(id, 'status_changed', {
          description: 'Uppdraget återställt till preliminär planering vid ombokning'
        })
      }

      // Handle revertToPlanned - revert TG statuses from ej_rapporterad to tilldelad
      if (result.revertToPlanned) {
        await supabase
          .from('assignment_subcontractors')
          .update({ tg_status: 'tilldelad' })
          .eq('assignment_id', id)
          .eq('tg_status', 'ej_rapporterad')

        // Log the revert
        await logAssignmentChange(id, 'status_changed', {
          description: 'Uppdraget återställt från Pågående till Planerad vid datumändring'
        })
      }

      if (!result.resetToPreliminar) {
        // Handle individual TG actions
        let smsSentCount = 0
        const assignmentIdentifier = assignmentNumber ? `#${assignmentNumber} ${formData.title}` : formData.title

        // Format date/time for SMS based on change type
        let originalDateTime: string
        let newDateTime: string

        if (isTimeChangeOnly && pendingTimeChange) {
          // Time-only change
          const originalTimeFormatted = originalData?.start_time ? originalData.start_time.substring(0, 5) : ''
          const originalEndFormatted = originalData?.end_time ? originalData.end_time.substring(0, 5) : ''
          const newTimeFormatted = pendingTimeChange.newStartTime.substring(0, 5)
          const newEndFormatted = pendingTimeChange.newEndTime ? pendingTimeChange.newEndTime.substring(0, 5) : ''

          originalDateTime = `${formData.start_date} kl ${originalTimeFormatted} - ${originalEndFormatted}`
          newDateTime = `${formData.start_date} kl ${newTimeFormatted} - ${newEndFormatted}`
        } else {
          // Date change (with or without time)
          const formattedTime = formData.start_time ? formData.start_time.substring(0, 5) : ''
          originalDateTime = formattedTime ? `${originalData?.start_date} ${formattedTime}` : originalData?.start_date || ''
          newDateTime = formattedTime ? `${pendingDateChange} ${formattedTime}` : pendingDateChange || ''
        }

        for (const [tgId, action] of Object.entries(result.tgActions)) {
          const report = subcontractorReports.find(r => r.id === tgId)
          const tgData = report?.subcontractors as any

          if (action.action === 'remove') {
            // Send SMS to removed TG first (before deleting)
            if (result.sendSmsTo[tgId] && tgData?.phone) {
              const reason = action.removeReason || ''
              const removeMessage = reason
                ? `Du har tagits bort från uppdraget ${assignmentIdentifier} (planerat ${originalDateTime}). ${reason}`
                : `Du har tagits bort från uppdraget ${assignmentIdentifier} (planerat ${originalDateTime}).`
              try {
                await sendSMS({
                  recipient_type: 'subcontractor',
                  recipient_id: report!.subcontractor_id,
                  recipient_name: tgData.name || 'TG',
                  recipient_phone: tgData.phone,
                  message: removeMessage,
                  assignment_id: id
                })
                smsSentCount++
              } catch (smsError) {
                console.error('Error sending SMS to removed TG:', smsError)
              }
            }

            // Delete TG from assignment
            await supabase
              .from('assignment_subcontractors')
              .delete()
              .eq('id', tgId)

            // Log the removal
            await logAssignmentChange(id, 'subcontractor_removed', {
              description: `TG borttagen vid ombokning: ${action.removeReason || 'Ingen anledning angiven'}`
            })
          } else if (action.action === 'keep' && result.sendSmsTo[tgId]) {
            // Send SMS to TGs being kept about the change (per-TG setting)
            if (tgData?.phone) {
              // Build message with optional extra info
              const baseMessage = isTimeChangeOnly
                ? `Uppdraget ${assignmentIdentifier} har ändrat tid från ${originalDateTime} till ${newDateTime}.`
                : `Uppdraget ${assignmentIdentifier} har flyttats från ${originalDateTime} till ${newDateTime}.`
              const keepMessage = action.extraMessage
                ? `${baseMessage} ${action.extraMessage}`
                : baseMessage
              try {
                await sendSMS({
                  recipient_type: 'subcontractor',
                  recipient_id: report!.subcontractor_id,
                  recipient_name: tgData.name || 'TG',
                  recipient_phone: tgData.phone,
                  message: keepMessage,
                  assignment_id: id
                })
                smsSentCount++
              } catch (smsError) {
                console.error('Error sending SMS to TG:', smsError)
              }
            }
          }
        }

        if (smsSentCount > 0) {
          toast.success(`SMS skickat till ${smsSentCount} TG${smsSentCount > 1 ? ':s' : ''}`)
        }
      }

      // Update local state
      // Build the updates for both formData and originalData
      const stateUpdates: Record<string, any> = {
        ...(result.resetToPreliminar && { status: 'preliminar' as const }),
        ...(result.revertToPlanned && { status: 'planerad' as const }),
        ...(result.customerAction === 'reset_to_ej_kontaktad' && {
          customer_comm_status: 'ej_kontaktad' as CustomerCommStatus,
          customer_informed_at: null,
          customer_confirmed_at: null
        }),
        ...(result.customerAction === 'set_informerad' && {
          customer_comm_status: 'informerad' as CustomerCommStatus,
          customer_informed_at: now
        })
      }

      // Add the specific change (date or time)
      if (isTimeChangeOnly && pendingTimeChange) {
        stateUpdates.start_time = pendingTimeChange.newStartTime
        stateUpdates.end_time = pendingTimeChange.newEndTime
      } else if (pendingDateChange) {
        stateUpdates.start_date = pendingDateChange
      }

      setFormData(prev => ({ ...prev, ...stateUpdates }))
      setOriginalData(prev => prev ? { ...prev, ...stateUpdates } : prev)

      // Log the change
      if (isTimeChangeOnly && pendingTimeChange) {
        await logAssignmentChange(id, 'field_changed', {
          fieldName: 'start_time',
          oldValue: `${originalData?.start_time}-${originalData?.end_time}`,
          newValue: `${pendingTimeChange.newStartTime}-${pendingTimeChange.newEndTime}`,
          description: result.resetToPreliminar
            ? 'Tid ändrad och uppdraget återställt till preliminär planering'
            : 'Tid ändrad'
        })
      } else {
        await logAssignmentChange(id, 'field_changed', {
          fieldName: 'start_date',
          oldValue: originalData?.start_date,
          newValue: pendingDateChange || undefined,
          description: result.resetToPreliminar
            ? 'Datum ändrat och uppdraget återställt till preliminär planering'
            : 'Datum ändrat (ombokning)'
        })
      }

      // Refresh subcontractor data
      const removedCount = Object.values(result.tgActions).filter(a => a.action === 'remove').length
      if (removedCount > 0 || result.resetToPreliminar || result.revertToPlanned) {
        queryClient.invalidateQueries({ queryKey: ['assignment-reports', id] })
      }

      // Show appropriate toast message
      const changeType = isTimeChangeOnly ? 'Tid' : 'Datum'
      if (result.resetToPreliminar) {
        toast.success(`${changeType} ändrad och uppdraget återställt till preliminär`)
      } else if (result.revertToPlanned) {
        toast.success(`${changeType} ändrad och uppdraget återställt till Planerad`)
      } else {
        toast.success(`${changeType} ändrad`)
      }
    } catch (error) {
      console.error('Error handling rebooking:', error)
      toast.error(isTimeChangeOnly ? 'Kunde inte spara tidändring' : 'Kunde inte spara datumändring')
    } finally {
      setPendingDateChange(null)
      setPendingTimeChange(null)
      setIsTimeChangeOnly(false)
    }
  }

  // Show planning confirmation modal
  const handleConfirmPlanning = () => {
    if (!id || isNewAssignment) return
    if (formData.status !== 'preliminar') return

    // Check for unsaved changes first
    if (hasUnsavedChanges()) {
      toast.error('Spara ändringar först innan du bekräftar planeringen')
      return
    }

    setShowPlanningModal(true)
  }

  // Actually confirm planning and send SMS
  const handlePlanningConfirmed = async (result: PlanningConfirmationResult) => {
    if (!id || isNewAssignment) return

    try {
      // Update assignment status to planerad
      const { error: assignmentError } = await supabase
        .from('assignments')
        .update({
          status: 'planerad',
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      if (assignmentError) throw assignmentError

      // Update TG statuses from preliminar_tilldelad to tilldelad
      const { error: tgError } = await supabase
        .from('assignment_subcontractors')
        .update({ tg_status: 'tilldelad' })
        .eq('assignment_id', id)
        .eq('tg_status', 'preliminar_tilldelad')

      if (tgError) throw tgError

      // Send SMS to selected TGs
      let smsCount = 0
      for (const report of subcontractorReports) {
        const sub = Array.isArray(report.subcontractors) ? report.subcontractors[0] : report.subcontractors
        const phone = sub?.phone

        if (phone && result.sendSmsTo[report.id]) {
          // Build SMS message
          const identifier = assignmentNumber ? `#${assignmentNumber} ${formData.title}` : formData.title
          const dateTime = formData.start_time
            ? `${formData.start_date} kl ${formData.start_time.substring(0, 5)}`
            : formData.start_date
          const addressPart = getLocationDisplay(formData.location) ? `${getLocationDisplay(formData.location)}, ` : ''
          const extra = result.extraInfo[report.id]?.trim()

          let message = `Du är tilldelad uppdraget ${identifier}. ${addressPart}${dateTime}.`
          if (extra) {
            message += ` ${extra}`
          }

          try {
            const smsResult = await sendSMS({
              message,
              recipient_type: 'subcontractor',
              recipient_id: report.subcontractor_id,
              recipient_name: sub?.name || 'Okänd',
              recipient_phone: phone,
              assignment_id: id
            })
            if (smsResult.success) smsCount++
          } catch (smsError) {
            console.error('SMS send error:', smsError)
          }
        }
      }

      // Log the change
      const smsInfo = smsCount > 0 ? ` (${smsCount} SMS skickade)` : ''
      await logAssignmentChange(id, 'status_changed', {
        fieldName: 'status',
        oldValue: 'preliminar',
        newValue: 'planerad',
        description: `Bekräftade planeringen${smsInfo}`
      })

      // Update local state
      setFormData(prev => ({ ...prev, status: 'planerad' }))
      setOriginalData(prev => prev ? { ...prev, status: 'planerad' } : prev)

      if (smsCount > 0) {
        toast.success(`Planering bekräftad! ${smsCount} SMS skickade`)
      } else {
        toast.success('Planering bekräftad!')
      }
      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      await refetchReports()
    } catch (error) {
      console.error('Error confirming planning:', error)
      toast.error('Kunde inte bekräfta planeringen')
    }
  }

  const handleDelete = () => {
    if (!id || isNewAssignment) return
    if (hasAnyReports) {
      toast.error('Kan inte ta bort uppdrag som har rapporter')
      return
    }
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!id || isNewAssignment) return

    try {
      setDeleting(true)
      const { error } = await supabase
        .from('assignments')
        .delete()
        .eq('id', id)

      if (error) throw error

      toast.success('Uppdrag borttaget')
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      afterDelete()
    } catch (error) {
      console.error('Error deleting assignment:', error)
      toast.error('Kunde inte ta bort uppdrag')
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleCopy = async () => {
    if (!id || isNewAssignment) return

    try {
      setSaving(true)

      // Calculate status for new assignment based on gardener and planned time (V2)
      const calculatedStatus = calculateAssignmentStatusV2({
        status: 'ohanterad',
        start_date: formData.start_date || null,
        start_time: formData.start_time || null,
        subcontractor_ids: formData.subcontractor_ids || [],
        customer_comm_status: 'ej_kontaktad',
        customer_invoice_status: 'ej_fakturerad'
      })

      // Create new assignment with copied data
      const assignmentData = {
        title: `${formData.title} (Kopia)`,
        description: formData.description || null,
        customer_id: formData.customer_id, // Default to original customer
        status: calculatedStatus,
        start_date: formData.start_date || null,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        location: formData.location || null
      }

      const { data: newAssignment, error: assignmentError } = await supabase
        .from('assignments')
        .insert(assignmentData)
        .select()
        .single()

      if (assignmentError) throw assignmentError

      // Copy subcontractors
      if (formData.subcontractor_ids.length > 0) {
        const subInserts = formData.subcontractor_ids.map(subId => ({
          assignment_id: newAssignment.id,
          subcontractor_id: subId
        }))
        const { error: subError } = await supabase
          .from('assignment_subcontractors')
          .insert(subInserts)
        if (subError) throw subError
      }

      // Copy articles
      if (plannedArticles.length > 0) {
        const articleInserts = plannedArticles.map(item => ({
          assignment_id: newAssignment.id,
          article_id: item.articleId,
          quantity: item.quantity,
          custom_price: item.customPrice,
          notes: item.notes,
          exclude_from_tg_reporting: item.excludeFromTgReporting || false,
          subcontractor_prices: item.subcontractorPrices || {},
          subcontractor_quantities: item.subcontractorQuantities || {}
        }))
        const { error: articleError } = await supabase
          .from('assignment_articles')
          .insert(articleInserts)
        if (articleError) throw articleError
      }

      toast.success('Uppdrag kopierat!')
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      goToAssignment(newAssignment.id)
    } catch (error) {
      console.error('Error copying assignment:', error)
      toast.error('Kunde inte kopiera uppdrag')
    } finally {
      setSaving(false)
    }
  }

  const getLocationDisplay = (location: string | { address: string; coordinates?: { lat: number; lng: number } }) => {
    return typeof location === 'string' ? location : location?.address || ''
  }

  const addSubcontractor = async (subId: string) => {
    if (!formData.subcontractor_ids.includes(subId)) {
      const sub = subcontractors.find(s => s.id === subId)

      // Check if assignment is in a confirmed state - show modal for status selection
      const confirmedStatuses = ['planerad', 'pagaende', 'granskning']
      if (!isNewAssignment && confirmedStatuses.includes(formData.status)) {
        // Show modal to select TG status
        setPendingSubcontractor({
          id: subId,
          name: sub?.name || 'Okänd',
          phone: sub?.phone || null
        })
        setShowAddSubcontractorModal(true)
        return
      }

      // Normal flow - add directly
      await doAddSubcontractor(subId)
    }
  }

  // Actually add the subcontractor (called directly or after modal confirmation)
  const doAddSubcontractor = async (subId: string, customStatus?: TgWorkStatus) => {
    const newSubcontractorIds = [...formData.subcontractor_ids, subId]

    setFormData(prev => ({
      ...prev,
      subcontractor_ids: [...prev.subcontractor_ids, subId]
    }))

    // Store custom status if provided
    if (customStatus) {
      setCustomTgStatuses(prev => ({
        ...prev,
        [subId]: customStatus
      }))
    }

    // Fetch TG's price list prices and prefill
    const sub = subcontractors.find(s => s.id === subId)
    if (sub?.price_list_id && plannedArticles.length > 0) {
      try {
        const articleIds = plannedArticles.map(a => a.articleId)
        const { data: priceData } = await supabase
          .from('price_list_articles')
          .select('article_id, price_excl_vat')
          .eq('price_list_id', sub.price_list_id)
          .in('article_id', articleIds)

        if (priceData && priceData.length > 0) {
          const priceMap = new Map(priceData.map(p => [p.article_id, p.price_excl_vat]))
          setPlannedArticles(prev => prev.map(article => {
            const tgPrice = priceMap.get(article.articleId)
            if (tgPrice !== undefined) {
              return {
                ...article,
                subcontractorPrices: {
                  ...(article.subcontractorPrices || {}),
                  [subId]: tgPrice
                }
              }
            }
            return article
          }))
        }
      } catch (error) {
        console.error('Error fetching TG price list:', error)
      }
    }

    // Handle per-TG quantity distribution when adding TG
    if (plannedArticles.length > 0) {
      const preliminaryStatuses = ['ohanterad', 'planering', 'preliminar']
      const isPreliminary = preliminaryStatuses.includes(formData.status)

      if (isPreliminary) {
        // Auto-redistribute quantities evenly among all TGs (including new one)
        setPlannedArticles(prev => prev.map(article => ({
          ...article,
          subcontractorQuantities: distributeQuantityEvenly(article.quantity, newSubcontractorIds)
        })))
      } else {
        // Planned/confirmed: Set new TG's quantity to 0, show warning modal
        setPlannedArticles(prev => prev.map(article => {
          const newQuantities = { ...(article.subcontractorQuantities || {}) }
          newQuantities[subId] = 0
          return {
            ...article,
            subcontractorQuantities: newQuantities
          }
        }))
        const sub = subcontractors.find(s => s.id === subId)
        setTgQuantityWarningData({
          type: 'add',
          subId,
          subName: sub?.name || 'Okänd'
        })
        setShowTgQuantityWarning(true)
      }
    }
  }

  // Handle modal confirmation for adding TG to confirmed assignment
  const handleAddSubcontractorConfirm = async (result: AddSubcontractorResult) => {
    if (!pendingSubcontractor) return

    const subId = pendingSubcontractor.id
    const subPhone = pendingSubcontractor.phone

    // Add the subcontractor with custom status
    await doAddSubcontractor(subId, result.tgStatus)

    // Send SMS if requested
    if (result.sendSms && subPhone) {
      try {
        const identifier = assignmentNumber ? `#${assignmentNumber} ${formData.title}` : formData.title
        const dateTimeStr = formData.start_date
          ? `${formData.start_date}${formData.start_time ? ' kl ' + formData.start_time.substring(0, 5) : ''}`
          : ''

        let message = ''
        if (result.tgStatus === 'preliminar_tilldelad') {
          message = `Hej! Du har blivit preliminärt tilldelad uppdraget ${identifier}${dateTimeStr ? ` (${dateTimeStr})` : ''}. Vänligen bekräfta i appen.`
        } else if (result.tgStatus === 'tilldelad') {
          message = `Hej! Du har blivit tilldelad uppdraget ${identifier}${dateTimeStr ? ` (${dateTimeStr})` : ''}. Detaljer finns i appen.`
        } else if (result.tgStatus === 'ej_rapporterad') {
          message = `Hej! Du har lagts till på uppdraget ${identifier}${dateTimeStr ? ` (${dateTimeStr})` : ''} som pågår. Glöm inte att rapportera när du är klar.`
        }

        if (message) {
          await sendSMS({
            recipient_phone: subPhone,
            recipient_type: 'subcontractor',
            recipient_id: subId,
            recipient_name: pendingSubcontractor.name,
            message,
            assignment_id: id || undefined
          })
          toast.success('SMS skickat till trädgårdsmästaren')
        }
      } catch (error) {
        console.error('Error sending SMS:', error)
        toast.error('Kunde inte skicka SMS')
      }
    }

    // Clear pending state
    setPendingSubcontractor(null)
    setShowAddSubcontractorModal(false)
  }

  const removeSubcontractor = (subId: string) => {
    const preliminaryStatuses = ['ohanterad', 'planering', 'preliminar']
    const isPreliminary = preliminaryStatuses.includes(formData.status)
    const remainingSubcontractorIds = formData.subcontractor_ids.filter(id => id !== subId)

    // For planned assignments with articles and remaining TGs, show confirmation modal
    if (!isPreliminary && plannedArticles.length > 0 && remainingSubcontractorIds.length > 0) {
      const sub = subcontractors.find(s => s.id === subId)
      setTgQuantityWarningData({
        type: 'remove',
        subId,
        subName: sub?.name || 'Okänd'
      })
      setShowTgQuantityWarning(true)
      return
    }

    // Otherwise, remove directly
    doRemoveSubcontractor(subId)
  }

  const doRemoveSubcontractor = (subId: string) => {
    const remainingSubcontractorIds = formData.subcontractor_ids.filter(id => id !== subId)

    setFormData(prev => ({
      ...prev,
      subcontractor_ids: prev.subcontractor_ids.filter(id => id !== subId)
    }))

    // Handle per-TG quantity redistribution when removing TG
    if (plannedArticles.length > 0) {
      const preliminaryStatuses = ['ohanterad', 'planering', 'preliminar']
      const isPreliminary = preliminaryStatuses.includes(formData.status)

      if (isPreliminary && remainingSubcontractorIds.length > 0) {
        // Auto-redistribute quantities evenly among remaining TGs
        setPlannedArticles(prev => prev.map(article => ({
          ...article,
          subcontractorQuantities: distributeQuantityEvenly(article.quantity, remainingSubcontractorIds)
        })))
      } else if (!isPreliminary) {
        // Planned/confirmed: Just remove their quantity entry
        setPlannedArticles(prev => prev.map(article => {
          if (!article.subcontractorQuantities) return article
          const newQuantities = { ...article.subcontractorQuantities }
          delete newQuantities[subId]
          return {
            ...article,
            subcontractorQuantities: Object.keys(newQuantities).length > 0 ? newQuantities : undefined
          }
        }))
      } else {
        // No remaining TGs in preliminary - clear all per-TG quantities
        setPlannedArticles(prev => prev.map(article => ({
          ...article,
          subcontractorQuantities: undefined
        })))
      }
    }
  }

  // Helper function to distribute quantity evenly among TGs (with decimals)
  const distributeQuantityEvenly = (totalQuantity: number, subcontractorIds: string[]): Record<string, number> => {
    if (subcontractorIds.length === 0) return {}

    // Divide evenly with decimals, round to 2 decimal places
    const perTg = Math.round((totalQuantity / subcontractorIds.length) * 100) / 100

    const quantities: Record<string, number> = {}
    subcontractorIds.forEach(id => {
      quantities[id] = perTg
    })

    return quantities
  }

  const addArticle = async (articleId: string, quantity: number = 1) => {
    const existing = plannedArticles.find(a => a.articleId === articleId)
    if (existing) {
      setPlannedArticles(prev =>
        prev.map(a => a.articleId === articleId
          ? { ...a, quantity: a.quantity + quantity }
          : a
        )
      )
    } else {
      // Fetch TG prices from their price lists
      const subcontractorPrices: Record<string, number> = {}

      if (formData.subcontractor_ids.length > 0) {
        const subsWithPriceLists = subcontractors
          .filter(s => formData.subcontractor_ids.includes(s.id) && s.price_list_id)

        if (subsWithPriceLists.length > 0) {
          try {
            const priceListIds = subsWithPriceLists.map(s => s.price_list_id).filter(Boolean) as string[]
            const { data: priceData } = await supabase
              .from('price_list_articles')
              .select('price_list_id, price_excl_vat')
              .eq('article_id', articleId)
              .in('price_list_id', priceListIds)

            if (priceData) {
              const priceByList = new Map(priceData.map(p => [p.price_list_id, p.price_excl_vat]))
              subsWithPriceLists.forEach(sub => {
                const price = priceByList.get(sub.price_list_id!)
                if (price !== undefined) {
                  subcontractorPrices[sub.id] = price
                }
              })
            }
          } catch (error) {
            console.error('Error fetching TG prices for article:', error)
          }
        }
      }

      // Distribute quantity evenly among assigned TGs
      const subcontractorQuantities = formData.subcontractor_ids.length > 0
        ? distributeQuantityEvenly(quantity, formData.subcontractor_ids)
        : undefined

      setPlannedArticles(prev => [...prev, {
        articleId,
        quantity,
        subcontractorPrices: Object.keys(subcontractorPrices).length > 0 ? subcontractorPrices : undefined,
        subcontractorQuantities
      }])
    }
    setShowArticleSelector(false)
  }

  const removeArticle = (articleId: string) => {
    setPlannedArticles(prev => prev.filter(a => a.articleId !== articleId))
  }

  const updateArticleQuantity = (articleId: string, quantity: number) => {
    setPlannedArticles(prev =>
      prev.map(a => {
        if (a.articleId !== articleId) return a
        // Auto-redistribute when total changes
        if (formData.subcontractor_ids.length > 0) {
          return {
            ...a,
            quantity,
            subcontractorQuantities: distributeQuantityEvenly(quantity, formData.subcontractor_ids)
          }
        }
        return { ...a, quantity }
      })
    )
  }

  const toggleArticleTgExclusion = (articleId: string) => {
    setPlannedArticles(prev =>
      prev.map(a => a.articleId === articleId
        ? { ...a, excludeFromTgReporting: !a.excludeFromTgReporting }
        : a)
    )
  }

  // Update quantity for a specific TG on an article (and auto-update total)
  const updateSubcontractorQuantity = (articleId: string, subcontractorId: string, quantity: number) => {
    setPlannedArticles(prev =>
      prev.map(a => {
        if (a.articleId !== articleId) return a
        const newQuantities = { ...(a.subcontractorQuantities || {}) }
        newQuantities[subcontractorId] = quantity
        // Calculate new total from per-TG quantities
        const newTotal = Object.values(newQuantities).reduce((sum, q) => sum + q, 0)
        return {
          ...a,
          subcontractorQuantities: newQuantities,
          quantity: newTotal  // Auto-update total to match sum
        }
      })
    )

    // Show indicator that total was auto-updated
    setAutoUpdatedTotals(prev => new Set(prev).add(articleId))
    setTimeout(() => {
      setAutoUpdatedTotals(prev => {
        const next = new Set(prev)
        next.delete(articleId)
        return next
      })
    }, 3000)
  }

  // Redistribute article quantity evenly among assigned TGs
  const redistributeArticleQuantity = (articleId: string) => {
    setPlannedArticles(prev =>
      prev.map(a => {
        if (a.articleId !== articleId) return a
        const newQuantities = distributeQuantityEvenly(a.quantity, formData.subcontractor_ids)
        return { ...a, subcontractorQuantities: newQuantities }
      })
    )
  }


  // Insert text at cursor position in description
  const insertAtCursor = (textToInsert: string) => {
    const textarea = descriptionRef.current
    if (!textarea) {
      // Fallback: append to end
      setFormData(prev => ({
        ...prev,
        description: prev.description ? `${prev.description}${textToInsert}` : textToInsert
      }))
      return
    }

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const currentValue = formData.description

    const newValue = currentValue.substring(0, start) + textToInsert + currentValue.substring(end)

    setFormData(prev => ({ ...prev, description: newValue }))

    // Restore cursor position after the inserted text
    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = textarea.selectionEnd = start + textToInsert.length
    }, 0)
  }

  // Import article descriptions to the description field
  const importArticlesToDescription = () => {
    if (plannedArticles.length === 0) {
      toast.error('Inga artiklar att importera')
      return
    }

    const articleLines = plannedArticles.map(pa => {
      const article = articles.find(a => a.id === pa.articleId)
      if (!article) return null
      const qty = article.type === 'hourly' ? (pa.estimatedHours || pa.quantity) : pa.quantity
      return `${qty} ${article.unit || 'st'} - ${article.name}${article.description ? ` - ${article.description}` : ''}`
    }).filter(Boolean)

    if (articleLines.length === 0) return

    const articleText = `--- Artiklar ---\n${articleLines.join('\n')}`

    insertAtCursor(articleText)
    toast.success('Artiklar importerade till beskrivningen')
  }

  // Fetch previous assignments for the same customer
  const fetchPreviousAssignments = async () => {
    if (!formData.customer_id) {
      toast.error('Välj en kund först')
      return
    }

    setLoadingPreviousAssignments(true)
    try {
      // Fetch assignments for this customer (pågående and later statuses)
      const completedStatuses = ['pagaende', 'granskning', 'attesterad', 'slutford']
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          description,
          start_date,
          status,
          assignment_subcontractors (
            subcontractors (name, phone)
          ),
          assignment_articles (
            quantity,
            articles (name, unit)
          )
        `)
        .eq('customer_id', formData.customer_id)
        .in('status', completedStatuses)
        .neq('id', id || '') // Exclude current assignment
        .order('start_date', { ascending: false })
        .limit(10)

      if (error) throw error

      const mapped = (data || []).map(a => ({
        id: a.id,
        assignment_number: a.assignment_number,
        title: a.title,
        description: a.description,
        start_date: a.start_date,
        status: a.status,
        subcontractors: (a.assignment_subcontractors || []).map((as: any) => ({
          name: as.subcontractors?.name || 'Okänd',
          phone: as.subcontractors?.phone || null
        })),
        articles: (a.assignment_articles || []).map((aa: any) => ({
          name: aa.articles?.name || 'Okänd',
          quantity: aa.quantity,
          unit: aa.articles?.unit
        }))
      }))

      setPreviousAssignments(mapped)
      setSelectedPreviousAssignments(new Set()) // Reset selection
      setShowPreviousAssignmentsModal(true)
    } catch (error) {
      console.error('Error fetching previous assignments:', error)
      toast.error('Kunde inte hämta tidigare uppdrag')
    } finally {
      setLoadingPreviousAssignments(false)
    }
  }

  // Extract clean description (without Historik and Artiklar sections)
  const getCleanDescription = (description: string | null): string => {
    if (!description) return ''

    // Find the first occurrence of "--- Historik ---" or "--- Artiklar ---"
    const historikIndex = description.indexOf('--- Historik ---')
    const artiklarIndex = description.indexOf('--- Artiklar ---')

    // Find the earliest index where we should cut
    let cutIndex = description.length
    if (historikIndex !== -1) cutIndex = Math.min(cutIndex, historikIndex)
    if (artiklarIndex !== -1) cutIndex = Math.min(cutIndex, artiklarIndex)

    return description.substring(0, cutIndex).trim()
  }

  // Toggle selection of a previous assignment
  const togglePreviousAssignmentSelection = (assignmentId: string) => {
    setSelectedPreviousAssignments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId)
      } else {
        newSet.add(assignmentId)
      }
      return newSet
    })
  }

  // Import selected previous assignments
  const importSelectedPreviousAssignments = () => {
    if (selectedPreviousAssignments.size === 0) {
      toast.error('Välj minst ett uppdrag att importera')
      return
    }

    const selectedAssignments = previousAssignments.filter(a => selectedPreviousAssignments.has(a.id))

    const importSections = selectedAssignments.map(assignment => {
      const lines: string[] = []

      // Header: #ID Titel (datum)
      const idDisplay = assignment.assignment_number ? `#${assignment.assignment_number}` : assignment.id.slice(0, 8)
      lines.push(`${idDisplay} ${assignment.title} (${assignment.start_date || 'okänt datum'})`)

      // Subcontractors with phone
      if (assignment.subcontractors.length > 0) {
        const tgLine = assignment.subcontractors
          .map(s => s.phone ? `${s.name} (${s.phone})` : s.name)
          .join(', ')
        lines.push(`TG: ${tgLine}`)
      }

      // Articles
      if (assignment.articles.length > 0) {
        const articleLines = assignment.articles
          .map(a => `${a.quantity} ${a.unit || 'st'} - ${a.name}`)
          .join(', ')
        lines.push(`Artiklar: ${articleLines}`)
      }

      // Clean description (without Historik/Artiklar sections)
      const cleanDesc = getCleanDescription(assignment.description)
      if (cleanDesc) {
        lines.push(`Beskrivning: ${cleanDesc}`)
      }

      return lines.join('\n')
    })

    if (importSections.length === 0) {
      toast.error('Inga uppgifter att importera')
      return
    }

    const fullImportText = `--- Historik ---\n${importSections.join('\n\n---\n\n')}`

    insertAtCursor(fullImportText)
    setShowPreviousAssignmentsModal(false)
    toast.success(`Importerade ${selectedAssignments.length} uppdrag`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar uppdrag...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header - #123456 - Titel                    [Planerad ▼] */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {!isNewAssignment && assignmentNumber && (
                <span className="text-sm font-mono text-nature-500 dark:text-nature-400">
                  #{assignmentNumber}
                </span>
              )}
              {!isNewAssignment && assignmentNumber && (
                <span className="text-nature-400 dark:text-nature-500">-</span>
              )}
              <h1 className="text-lg sm:text-xl font-bold text-nature-900 dark:text-nature-100 truncate">
                {isNewAssignment ? 'Nytt uppdrag' : (formData.title || 'Uppdrag')}
              </h1>
            </div>
            {/* Adress, Datum, Tid - endast för befintliga uppdrag */}
            {!isNewAssignment && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-nature-600 dark:text-nature-400">
                {formData.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {typeof formData.location === 'string'
                      ? formData.location
                      : formData.location.address}
                  </span>
                )}
                {formData.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {format(new Date(formData.start_date), 'yyyy-MM-dd')}
                  </span>
                )}
                {(formData.start_time || formData.end_time) && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {(formData.start_time || '--:--').substring(0, 5)} - {(formData.end_time || '--:--').substring(0, 5)}
                  </span>
                )}
              </div>
            )}
            {isNewAssignment && (
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-0.5">
                Fyll i uppgifterna nedan
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            {/* Navigation between filtered assignments (with wrap-around) - hidden in modal */}
            {!isModal && !isNewAssignment && filteredAssignmentIds.length > 1 && (
              <div className="flex items-center gap-1 border-r border-nature-200 dark:border-nature-600 pr-2 mr-1">
                <button
                  onClick={navigateToPrevious}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Föregående uppdrag (Page Up)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-xs text-nature-500 dark:text-nature-400 min-w-[3rem] text-center">
                  {currentIndex + 1} / {filteredAssignmentIds.length}
                </span>
                <button
                  onClick={navigateToNext}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Nästa uppdrag (Page Down)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}

            {lastSaved && !hasUnsavedChanges() && (
              <span className="text-xs text-nature-500 dark:text-nature-400">
                Sparad {format(lastSaved, 'HH:mm')}
              </span>
            )}

            {/* Action buttons - only for existing assignments */}
            {!isNewAssignment && (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopy}
                  disabled={saving}
                  className="p-2 text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
                  title="Kopiera uppdrag"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDelete}
                  disabled={hasAnyReports}
                  className={`p-2 rounded-lg transition-colors ${
                    hasAnyReports
                      ? 'text-nature-400 dark:text-nature-600 cursor-not-allowed'
                      : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                  }`}
                  title={hasAnyReports ? 'Kan inte ta bort - uppdraget har rapporter' : 'Ta bort uppdrag'}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Status Badge + Workflow Info - V2 Status System */}
            {!isNewAssignment && (
              <div className="flex items-center gap-2">
                {/* Combined Status Badge with Workflow Button */}
                <button
                  onClick={() => setShowStatusSelector(!showStatusSelector)}
                  className={`
                    inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
                    ${getAssignmentStatusBadgeClassesV2(formData.status as AssignmentStatusV2)}
                    hover:ring-2 hover:ring-offset-1 hover:ring-nature-400 transition-all cursor-pointer
                  `}
                  title="Klicka för att visa statusflöde"
                >
                  {getAssignmentStatusNameV2(formData.status as AssignmentStatusV2)}
                  <Workflow className="h-4 w-4 opacity-60" />
                </button>
              </div>
            )}

            {/* Status Workflow Modal */}
            {showStatusSelector && !isNewAssignment && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowStatusSelector(false)}>
                <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-4 border-b border-nature-200 dark:border-nature-700">
                    <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 flex items-center gap-2">
                      <Workflow className="h-5 w-5" />
                      Statusflöde
                    </h3>
                    <button
                      onClick={() => setShowStatusSelector(false)}
                      className="p-1 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 rounded"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-4 space-y-6">
                    {/* Uppdragsstatus */}
                    <div>
                      <h4 className="text-sm font-semibold text-nature-700 dark:text-nature-300 mb-3">UPPDRAGSSTATUS</h4>
                      <div className="flex flex-wrap items-center gap-2">
                        {ASSIGNMENT_STATUSES_V2.filter(s => s.key !== 'avbruten').map((status, idx) => {
                          const isCurrent = formData.status === status.key
                          const statusOrder = ['ohanterad', 'planering', 'preliminar', 'planerad', 'pagaende', 'granskning', 'attesterad', 'slutford']
                          const currentIdx = statusOrder.indexOf(formData.status)
                          const thisIdx = statusOrder.indexOf(status.key)
                          const isPast = thisIdx < currentIdx

                          return (
                            <div key={status.key} className="flex items-center gap-1">
                              <div className={`
                                flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                                ${isCurrent
                                  ? status.bgColor + ' ring-2 ring-offset-1 ring-nature-500'
                                  : isPast
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                                }
                              `}>
                                {isPast && <CheckCircle className="h-3 w-3" />}
                                {isCurrent && <span className="w-2 h-2 rounded-full bg-current" />}
                                {status.name}
                              </div>
                              {idx < 7 && <span className="text-nature-300 dark:text-nature-600">→</span>}
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Kundstatus */}
                    <div>
                      <h4 className="text-sm font-semibold text-nature-700 dark:text-nature-300 mb-3 flex items-center gap-2">
                        <User className="h-4 w-4" />
                        KUNDSTATUS
                        {formData.customer_id && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowStatusSelector(false)
                              navigate(`/admin/customers/${formData.customer_id}`)
                            }}
                            className="font-normal text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline flex items-center gap-1"
                          >
                            — {customers.find(c => c.id === formData.customer_id)?.full_name || 'Okänd'}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        )}
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-nature-500 dark:text-nature-400">Kommunikation:</span>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {CUSTOMER_COMM_STATUSES
                              .filter(s => s.key !== 'avbokad' || formData.customer_comm_status === 'avbokad')
                              .map((status, idx, filteredArr) => {
                              const isCurrent = formData.customer_comm_status === status.key
                              const statusOrder = filteredArr.map(s => s.key)
                              const currentIdx = statusOrder.indexOf(formData.customer_comm_status)
                              const thisIdx = statusOrder.indexOf(status.key)
                              const isPast = thisIdx < currentIdx
                              return (
                                <div key={status.key} className="flex items-center gap-1">
                                  <div className={`
                                    flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                                    ${isCurrent
                                      ? status.bgColor + ' ring-2 ring-offset-1 ring-nature-500'
                                      : isPast
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                                    }
                                  `}>
                                    {isPast && <CheckCircle className="h-3 w-3" />}
                                    {isCurrent && <span className="w-2 h-2 rounded-full bg-current" />}
                                    {status.name}
                                  </div>
                                  {idx < filteredArr.length - 1 && <span className="text-nature-300 dark:text-nature-600">→</span>}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-nature-500 dark:text-nature-400 flex items-center gap-1">
                            <Receipt className="h-3 w-3" />
                            Fakturering:
                          </span>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {CUSTOMER_INVOICE_STATUSES.map((status, idx) => {
                              const isCurrent = formData.customer_invoice_status === status.key
                              const statusOrder = CUSTOMER_INVOICE_STATUSES.map(s => s.key)
                              const currentIdx = statusOrder.indexOf(formData.customer_invoice_status)
                              const thisIdx = statusOrder.indexOf(status.key)
                              const isPast = thisIdx < currentIdx
                              return (
                                <div key={status.key} className="flex items-center gap-1">
                                  <div className={`
                                    flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                                    ${isCurrent
                                      ? status.bgColor + ' ring-2 ring-offset-1 ring-nature-500'
                                      : isPast
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                                    }
                                  `}>
                                    {isPast && <CheckCircle className="h-3 w-3" />}
                                    {isCurrent && <span className="w-2 h-2 rounded-full bg-current" />}
                                    {status.name}
                                  </div>
                                  {idx < CUSTOMER_INVOICE_STATUSES.length - 1 && <span className="text-nature-300 dark:text-nature-600">→</span>}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Trädgårdsmästarstatus */}
                    {(formData.subcontractor_ids.length > 0 || subcontractorReports?.some(r => r.tg_status === 'avsagt')) && (
                      <div>
                        <h4 className="text-sm font-semibold text-nature-700 dark:text-nature-300 mb-3 flex items-center gap-2">
                          <Leaf className="h-4 w-4" />
                          TRÄDGÅRDSMÄSTARSTATUS
                        </h4>
                        {/* Active TGs (exclude declined) */}
                        {formData.subcontractor_ids
                          .filter(subId => {
                            const report = subcontractorReports?.find(r => r.subcontractor_id === subId)
                            return report?.tg_status !== 'avsagt'
                          })
                          .map(subId => {
                          const sub = subcontractors.find(s => s.id === subId)
                          const report = subcontractorReports?.find(r => r.subcontractor_id === subId)
                          const currentTgStatus = (report?.tg_status || 'tilldelad') as TgWorkStatus
                          const currentTgInvoiceStatus = (report?.tg_invoice_status || 'ej_fakturerad') as TgInvoiceStatus
                          return (
                            <div key={subId} className="mb-4 pl-2 border-l-2 border-nature-200 dark:border-nature-600" style={{ borderColor: sub?.color || undefined }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowStatusSelector(false)
                                  navigate(`/admin/subcontractors/${subId}`)
                                }}
                                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline flex items-center gap-1 mb-2"
                              >
                                {sub?.name || 'Okänd'}
                                <ExternalLink className="h-3 w-3" />
                              </button>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs text-nature-500 dark:text-nature-400">Arbete:</span>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {TG_WORK_STATUSES
                                      .filter(s => s.key !== 'avsagt' && (s.key !== 'komplettering' || currentTgStatus === 'komplettering'))
                                      .map((status, idx, filteredArr) => {
                                      const isCurrent = currentTgStatus === status.key
                                      const statusOrder = filteredArr.map(s => s.key)
                                      const currentIdx = statusOrder.indexOf(currentTgStatus)
                                      const thisIdx = statusOrder.indexOf(status.key)
                                      const isPast = thisIdx < currentIdx
                                      return (
                                        <div key={status.key} className="flex items-center gap-1">
                                          <div className={`
                                            flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                                            ${isCurrent
                                              ? status.bgColor + ' ring-2 ring-offset-1 ring-nature-500'
                                              : isPast
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                                            }
                                          `}>
                                            {isPast && <CheckCircle className="h-3 w-3" />}
                                            {isCurrent && <span className="w-2 h-2 rounded-full bg-current" />}
                                            {status.name}
                                          </div>
                                          {idx < filteredArr.length - 1 && <span className="text-nature-300 dark:text-nature-600">→</span>}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-xs text-nature-500 dark:text-nature-400 flex items-center gap-1">
                                    <Receipt className="h-3 w-3" />
                                    Faktura:
                                  </span>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {TG_INVOICE_STATUSES.map((status, idx) => {
                                      const isCurrent = currentTgInvoiceStatus === status.key
                                      const statusOrder = TG_INVOICE_STATUSES.map(s => s.key)
                                      const currentIdx = statusOrder.indexOf(currentTgInvoiceStatus)
                                      const thisIdx = statusOrder.indexOf(status.key)
                                      const isPast = thisIdx < currentIdx
                                      return (
                                        <div key={status.key} className="flex items-center gap-1">
                                          <div className={`
                                            flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
                                            ${isCurrent
                                              ? status.bgColor + ' ring-2 ring-offset-1 ring-nature-500'
                                              : isPast
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-nature-100 text-nature-500 dark:bg-nature-700 dark:text-nature-400'
                                            }
                                          `}>
                                            {isPast && <CheckCircle className="h-3 w-3" />}
                                            {isCurrent && <span className="w-2 h-2 rounded-full bg-current" />}
                                            {status.name}
                                          </div>
                                          {idx < TG_INVOICE_STATUSES.length - 1 && <span className="text-nature-300 dark:text-nature-600">→</span>}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}

                        {/* Declined TGs */}
                        {subcontractorReports?.filter(r => r.tg_status === 'avsagt').map(report => {
                          const sub = subcontractors.find(s => s.id === report.subcontractor_id)
                          return (
                            <div key={report.subcontractor_id} className="mb-4 pl-2 border-l-2 border-red-300 dark:border-red-700 opacity-60">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowStatusSelector(false)
                                  navigate(`/admin/subcontractors/${report.subcontractor_id}`)
                                }}
                                className="text-sm font-medium text-nature-500 dark:text-nature-400 hover:text-nature-700 dark:hover:text-nature-300 hover:underline flex items-center gap-1 mb-2"
                              >
                                {sub?.name || 'Okänd'}
                                <ExternalLink className="h-3 w-3" />
                              </button>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs text-nature-500 dark:text-nature-400">Arbete:</span>
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 ring-2 ring-offset-1 ring-red-500">
                                      <UserX className="h-3 w-3" />
                                      Avsagt
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Legend */}
                    <div className="pt-4 border-t border-nature-200 dark:border-nature-700">
                      <div className="flex items-center gap-4 text-xs text-nature-500 dark:text-nature-400">
                        <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" /> Klar</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-nature-500" /> Nuvarande</span>
                        <span>○ Väntar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => {
                if (hasUnsavedChanges()) {
                  if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
                    goBack()
                  }
                } else {
                  goBack()
                }
              }}
              className={`flex items-center justify-center space-x-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded-lg transition-colors w-full sm:w-auto ${
                hasUnsavedChanges()
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700'
                  : 'btn-outline'
              }`}
            >
              {hasUnsavedChanges() ? (
                <>
                  <X className="h-4 w-4" />
                  <span>Avbryt</span>
                </>
              ) : isModal ? (
                <>
                  <X className="h-4 w-4" />
                  <span>Stäng</span>
                </>
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  <span>Tillbaka</span>
                </>
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || formData.status === 'slutford'}
              className={`flex items-center justify-center space-x-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded-lg transition-colors w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white ${
                formData.status === 'slutford' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={formData.status === 'slutford' ? 'Uppdraget är låst - lås upp för att redigera' : undefined}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : formData.status === 'slutford' ? (
                <Lock className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Sparar...' : formData.status === 'slutford' ? 'Låst' : 'Spara'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Locked banner when assignment is completed */}
      {formData.status === 'slutford' && !isNewAssignment && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                Uppdraget är slutfört och låst
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                Ändringar kan endast göras genom att låsa upp uppdraget
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowUnlockModal(true)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
          >
            <Unlock className="h-4 w-4" />
            Lås upp
          </button>
        </div>
      )}

      {/* Unlock modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4 flex items-center gap-2">
              <Unlock className="h-5 w-5" />
              Lås upp uppdrag
            </h3>
            <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
              Ange orsak till varför uppdraget behöver låsas upp:
            </p>
            <textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100"
              rows={3}
              placeholder="T.ex. Kunden begärde ändringar, felaktig rapportering..."
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={async () => {
                  if (!unlockReason.trim()) {
                    toast.error('Ange orsak till upplåsning')
                    return
                  }
                  setUnlocking(true)
                  try {
                    await supabase
                      .from('assignments')
                      .update({
                        status: 'attest',
                        updated_at: new Date().toISOString()
                      })
                      .eq('id', id)

                    await logAssignmentChange(id!, 'status_changed', {
                      fieldName: 'status',
                      oldValue: getStatusLabel('slutford'),
                      newValue: getStatusLabel('attest'),
                      description: `Låste upp uppdraget: ${unlockReason}`
                    })

                    setFormData(prev => ({ ...prev, status: 'attest' }))
                    setShowUnlockModal(false)
                    setUnlockReason('')
                    toast.success('Uppdrag upplåst')
                    queryClient.invalidateQueries({ queryKey: ['assignment', id] })
                    queryClient.invalidateQueries({ queryKey: ['activity-log', 'assignment', id] })
                  } catch (err) {
                    console.error(err)
                    toast.error('Kunde inte låsa upp uppdraget')
                  } finally {
                    setUnlocking(false)
                  }
                }}
                disabled={unlocking}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                Lås upp
              </button>
              <button
                onClick={() => {
                  setShowUnlockModal(false)
                  setUnlockReason('')
                }}
                className="px-4 py-2 bg-nature-200 dark:bg-nature-600 text-nature-700 dark:text-nature-200 rounded-lg"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - All sections on one page */}
      <div className="space-y-4">
        {/* GRUNDINFO */}
          {/* Basic Info */}
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
            <button
              onClick={() => setBasicInfoCollapsed(!basicInfoCollapsed)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors rounded-lg"
            >
              <h2 className="text-base font-semibold text-nature-900 dark:text-nature-100 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Uppdrag
              </h2>
              {basicInfoCollapsed ? (
                <ChevronDown className="h-4 w-4 text-nature-400" />
              ) : (
                <ChevronUp className="h-4 w-4 text-nature-400" />
              )}
            </button>

            {!basicInfoCollapsed && (
            <div className={`px-4 pb-4 space-y-4 ${isLocked ? 'opacity-60 pointer-events-none' : ''}`}>
              {/* Rad 1: Titel + Plats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Titel *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, title: e.target.value }))
                      if (validationErrors.title) setValidationErrors(prev => ({ ...prev, title: false }))
                    }}
                    disabled={isLocked}
                    className={`input-field ${validationErrors.title ? 'border-red-500 ring-1 ring-red-500' : ''} ${isLocked ? 'bg-nature-100 dark:bg-nature-700 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="T.ex. Gräsklippning, Häckklippning..."
                  />
                  {validationErrors.title && (
                    <p className="text-red-500 text-sm mt-1">Titel är obligatoriskt</p>
                  )}
                </div>
                <div className={isLocked ? 'opacity-60 pointer-events-none' : ''}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="form-label mb-0 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Plats
                    </label>
                    <div className="flex items-center gap-2">
                      {useCustomerAddress && customerAddress && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                          Kund
                        </span>
                      )}
                      {!useCustomerAddress && getLocationDisplay(formData.location) && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                          Annan
                        </span>
                      )}
                      {getLocationDisplay(formData.location) && (
                        <button
                          type="button"
                          onClick={() => {
                            const loc = getLocationDisplay(formData.location)
                            if (loc) {
                              window.open(`https://www.google.com/maps?q=${encodeURIComponent(loc)}`, '_blank')
                            }
                          }}
                          className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="Öppna i Google Maps"
                        >
                          <Navigation className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <AddressSearch
                    onSelect={(address) => {
                      const fullAddress = [address.street, address.postalCode, address.city].filter(Boolean).join(', ')
                      setFormData(prev => ({ ...prev, location: fullAddress }))
                      setUseCustomerAddress(false)
                    }}
                  />
                  <input
                    type="text"
                    value={getLocationDisplay(formData.location)}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, location: e.target.value }))
                      // Check if it matches customer address
                      if (customerAddress && e.target.value === customerAddress) {
                        setUseCustomerAddress(true)
                      } else {
                        setUseCustomerAddress(false)
                      }
                    }}
                    className="input-field mt-2"
                    placeholder="Adress"
                  />
                  {customerAddress && !useCustomerAddress && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, location: customerAddress }))
                        setUseCustomerAddress(true)
                      }}
                      className="mt-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Använd kundens adress
                    </button>
                  )}
                </div>
              </div>

              {/* Rad 2: Beskrivning + Datum/Tid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                    <label className="form-label mb-0">Beskrivning</label>
                    <div className="flex items-center gap-2 sm:gap-3">
                      {formData.customer_id && !isLocked && (
                        <button
                          type="button"
                          onClick={fetchPreviousAssignments}
                          disabled={loadingPreviousAssignments}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                          title="Hämta från tidigare uppdrag"
                        >
                          {loadingPreviousAssignments ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <History className="h-3 w-3" />
                          )}
                          Historik
                        </button>
                      )}
                      {plannedArticles.length > 0 && !isLocked && (
                        <button
                          type="button"
                          onClick={importArticlesToDescription}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                          title="Importera artiklar till beskrivning"
                        >
                          <Package className="h-3 w-3" />
                          Importera
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    ref={descriptionRef}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={isLocked}
                    className={`input-field ${isLocked ? 'bg-nature-100 dark:bg-nature-700 cursor-not-allowed opacity-60' : ''}`}
                    rows={6}
                    placeholder="Beskriv uppdraget..."
                  />
                </div>

                {/* Datum & Tid kolumn */}
                <div className={`space-y-4 ${isLocked ? 'opacity-60 pointer-events-none' : ''}`}>
                  {/* Datum */}
                  <div className="relative" ref={datePickerRef}>
                    <label className="form-label">Datum</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        className="flex-1 input-field text-left flex items-center justify-between"
                      >
                        <span className={formData.start_date ? 'text-nature-900 dark:text-nature-100' : 'text-nature-400'}>
                          {formData.start_date || 'Ej planerat'}
                        </span>
                        <Calendar className="h-4 w-4 text-nature-400" />
                      </button>
                      {/* Calendar Panel Button - only show for existing assignments */}
                      {!isNewAssignment && (
                        <button
                          type="button"
                          onClick={() => setShowCalendarPanel(true)}
                          className="px-3 py-2 text-nature-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="Öppna kalender med TG-tillgänglighet"
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                  {/* Calendar Picker Popup */}
                  {showDatePicker && (
                    <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 p-4">
                      {/* Month Navigation */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          type="button"
                          onClick={() => setDatePickerMonth(prev => subMonths(prev, 1))}
                          className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <span className="font-semibold text-nature-900 dark:text-nature-100">
                          {format(datePickerMonth, 'MMMM yyyy', { locale: sv })}
                        </span>
                        <button
                          type="button"
                          onClick={() => setDatePickerMonth(prev => addMonths(prev, 1))}
                          className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                      
                      {/* Day headers */}
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map(day => (
                          <div key={day} className="text-center text-xs font-medium text-nature-500 py-1">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      {/* Calendar Days */}
                      <div className="grid grid-cols-7 gap-1">
                        {(() => {
                          const monthStart = startOfMonth(datePickerMonth)
                          const monthEnd = endOfMonth(datePickerMonth)
                          const calStart = startOfWeek(monthStart, { weekStartsOn: 1 })
                          const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
                          
                          const days = []
                          let day = calStart
                          while (day <= calEnd) {
                            const currentDay = day
                            const isSelected = formData.start_date && isSameDay(currentDay, new Date(formData.start_date))
                            const isCurrentMonth = isSameMonth(currentDay, datePickerMonth)
                            const isTodayDate = isToday(currentDay)
                            
                            days.push(
                              <button
                                key={currentDay.toISOString()}
                                type="button"
                                onClick={() => {
                                  const dateStr = format(currentDay, 'yyyy-MM-dd')
                                  handleDateChange(dateStr)
                                  setShowDatePicker(false)
                                }}
                                className={`
                                  p-2 text-sm rounded-lg transition-colors
                                  ${isSelected 
                                    ? 'bg-primary-500 text-white font-semibold' 
                                    : isCurrentMonth 
                                      ? 'text-nature-900 dark:text-nature-100 hover:bg-nature-100 dark:hover:bg-nature-700' 
                                      : 'text-nature-400 dark:text-nature-600 hover:bg-nature-50 dark:hover:bg-nature-800'
                                  }
                                  ${isTodayDate && !isSelected ? 'ring-2 ring-primary-300 dark:ring-primary-700' : ''}
                                `}
                              >
                                {format(currentDay, 'd')}
                              </button>
                            )
                            day = addDays(day, 1)
                          }
                          return days
                        })()}
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-700 flex justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            const today = new Date()
                            handleDateChange(format(today, 'yyyy-MM-dd'))
                            setShowDatePicker(false)
                          }}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          Idag
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDatePicker(false)}
                          className="text-sm text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
                        >
                          Stäng
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Time Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Starttid</label>
                    <TimeDropdown
                      value={formData.start_time}
                      onChange={handleStartTimeChange}
                    />
                  </div>
                  <div>
                    <label className="form-label">Sluttid</label>
                    <TimeDropdown
                      value={formData.end_time}
                      onChange={handleEndTimeChange}
                    />
                  </div>
                </div>

                {/* Quick time presets */}
                <div>
                  <label className="form-label">Snabbtider</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Morgon', start: '08:00', end: '12:00' },
                      { label: 'Heldag', start: '08:00', end: '16:00' },
                      { label: 'Eftermiddag', start: '12:00', end: '16:00' },
                      { label: 'Sen', start: '13:00', end: '17:00' },
                    ].map(preset => {
                      const isActive = formData.start_time === preset.start && formData.end_time === preset.end
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => handleTimeChange(preset.start, preset.end)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary-500 text-white'
                              : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
                          }`}
                        >
                          {preset.label} ({preset.start}-{preset.end})
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Confirm Planning Button - only show when status is preliminar */}
                {formData.status === 'preliminar' && !isNewAssignment && (
                  <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-700">
                    <button
                      type="button"
                      onClick={handleConfirmPlanning}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Bekräfta planering
                    </button>
                    {formData.customer_comm_status !== 'bekraftad' && (
                      <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 text-center">
                        ⚠️ Kunden har inte bekräftat uppdraget
                      </p>
                    )}
                  </div>
                )}
                </div>
              </div>

              {/* Artiklar - subsection */}
              <div className="border-t border-nature-200 dark:border-nature-700 pt-4 mt-4" ref={articleSelectorRef}>
                <button
                  type="button"
                  onClick={() => setArticlesCollapsed(!articlesCollapsed)}
                  className="w-full flex items-center justify-between mb-2"
                >
                  <h3 className="text-sm font-semibold text-nature-700 dark:text-nature-300 flex items-center">
                    <Package className="h-4 w-4 mr-2" />
                    Artiklar
                    {plannedArticles.length > 0 && (
                      <span className="ml-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full text-sm font-medium">
                        {plannedArticles.length} st - {plannedArticles.reduce((sum, item) => {
                          const price = customerPrices[item.articleId] ?? 0
                          return sum + (price * item.quantity)
                        }, 0).toLocaleString('sv-SE')} kr
                      </span>
                    )}
                  </h3>
                  {articlesCollapsed ? (
                    <ChevronDown className="h-4 w-4 text-nature-400" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-nature-400" />
                  )}
                </button>
                {!articlesCollapsed && (
                <div className={isLocked ? 'opacity-60 pointer-events-none' : ''}>
            {/* Searchable article selector - now at top */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
              <input
                type="text"
                value={articleSearch}
                onChange={(e) => {
                  setArticleSearch(e.target.value)
                  setShowArticleSelector(true)
                }}
                onFocus={() => setShowArticleSelector(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowArticleSelector(false)
                    setArticleSearch('')
                  } else if (e.key === 'Enter') {
                    const filtered = articles
                      .filter(a => !plannedArticles.some(p => p.articleId === a.id))
                      .filter(a => a.name.toLowerCase().includes(articleSearch.toLowerCase()))
                    if (filtered.length > 0) {
                      addArticle(filtered[0].id)
                      setArticleSearch('')
                    }
                  }
                }}
                placeholder="Sök och lägg till artikel..."
                className="input-field pl-9"
              />
              {showArticleSelector && (
                <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-48 overflow-y-auto">
                  {articles
                    .filter(a => !plannedArticles.some(p => p.articleId === a.id))
                    .filter(a => a.name.toLowerCase().includes(articleSearch.toLowerCase()))
                    .map(article => (
                      <button
                        key={article.id}
                        type="button"
                        onClick={() => {
                          addArticle(article.id)
                          setArticleSearch('')
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center justify-between"
                      >
                        <span className="text-nature-900 dark:text-nature-100">{article.name}</span>
                      </button>
                    ))}
                  {articles
                    .filter(a => !plannedArticles.some(p => p.articleId === a.id))
                    .filter(a => a.name.toLowerCase().includes(articleSearch.toLowerCase()))
                    .length === 0 && (
                    <p className="px-4 py-2 text-nature-500 text-sm">
                      {articleSearch ? 'Ingen matchning' : 'Alla artiklar är redan tillagda'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Added articles list with prices */}
            {plannedArticles.length > 0 && (
              <div className="space-y-2 mb-4">
                {plannedArticles.map(item => {
                  const article = articles.find(a => a.id === item.articleId)
                  // Customer price: custom override > customer price list > standard price
                  const customerPrice = customerPrices[item.articleId] ?? 0
                  const lineTotal = customerPrice * item.quantity
                  const isExcluded = item.excludeFromTgReporting
                  const assignedSubs = formData.subcontractor_ids
                    .map(subId => subcontractors.find(s => s.id === subId))
                    .filter(Boolean) as typeof subcontractors
                  return (
                    <div key={item.articleId} className="space-y-1">
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isExcluded
                            ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                            : 'bg-nature-50 dark:bg-nature-700'
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <Package className={`h-4 w-4 flex-shrink-0 ${isExcluded ? 'text-orange-500' : 'text-nature-500'}`} />
                          {/* Total quantity input - left aligned */}
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              defaultValue={String(item.quantity).replace('.', ',')}
                              key={`${item.articleId}-${item.quantity}`}
                              onBlur={(e) => {
                                const input = e.target.value.replace(',', '.')
                                const parsed = parseFloat(input)
                                if (!isNaN(parsed) && parsed >= 0) {
                                  updateArticleQuantity(item.articleId, parsed)
                                }
                              }}
                              className={`w-16 input-field text-center font-medium ${
                                autoUpdatedTotals.has(item.articleId)
                                  ? 'ring-2 ring-primary-400 dark:ring-primary-500'
                                  : ''
                              }`}
                            />
                            <span className="text-sm text-nature-500">st</span>
                            {autoUpdatedTotals.has(item.articleId) && (
                              <span className="text-xs text-primary-600 dark:text-primary-400 animate-pulse">
                                ← uppdaterad
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`font-medium block truncate ${
                              isExcluded
                                ? 'text-orange-700 dark:text-orange-300'
                                : 'text-nature-900 dark:text-nature-100'
                            }`}>
                              {article?.name || 'Okänd artikel'}
                              {isExcluded && <span className="ml-2 text-xs">(ej tillgänglig för trädgårdsmästare vid rapportering)</span>}
                            </span>
                            {article?.description && (
                              <span className="text-xs text-nature-400 dark:text-nature-500 block truncate">
                                {article.description}
                              </span>
                            )}
                            <span className="text-sm text-nature-500">
                              {customerPrice} kr/st (kundpris)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-nature-700 dark:text-nature-300 w-20 text-right">
                            {lineTotal.toLocaleString('sv-SE')} kr
                          </span>
                          <button
                            onClick={() => toggleArticleTgExclusion(item.articleId)}
                            className={`p-1.5 rounded transition-colors ${
                              isExcluded
                                ? 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                                : 'text-nature-400 hover:text-orange-500 hover:bg-orange-100 dark:hover:bg-orange-900/20'
                            }`}
                            title={isExcluded ? 'Inkludera i trädgårdsmästarrapportering' : 'Exkludera från trädgårdsmästarrapportering'}
                          >
                            {isExcluded ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => removeArticle(item.articleId)}
                            className="p-1 text-nature-500 hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {/* Per-TG quantities and prices */}
                      {!isExcluded && assignedSubs.length > 0 && (
                        <div className="ml-8 pl-3 border-l-2 border-primary-200 dark:border-primary-800 space-y-2">
                          {/* Action buttons */}
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              type="button"
                              onClick={() => redistributeArticleQuantity(item.articleId)}
                              className="text-xs px-2 py-1 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                              title="Fördela totalantal jämnt mellan trädgårdsmästare"
                            >
                              Dela jämnt
                            </button>
                          </div>
                          {/* Per-TG list */}
                          {assignedSubs.map(sub => {
                            const tgPrice = item.subcontractorPrices?.[sub.id]
                            const tgQuantity = item.subcontractorQuantities?.[sub.id]
                            const hasCustomQuantity = tgQuantity !== undefined
                            const displayQuantity = hasCustomQuantity ? tgQuantity : item.quantity
                            const isZeroQuantity = hasCustomQuantity && tgQuantity === 0
                            return (
                              <div
                                key={sub.id}
                                className={`flex items-center gap-2 text-sm ${
                                  isZeroQuantity ? 'opacity-50' : ''
                                }`}
                              >
                                <Leaf className={`h-3 w-3 flex-shrink-0 ${
                                  isZeroQuantity ? 'text-orange-500' : 'text-primary-500'
                                }`} />
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={String(displayQuantity).replace('.', ',')}
                                  key={`${item.articleId}-${sub.id}-${displayQuantity}`}
                                  onBlur={(e) => {
                                    const input = e.target.value.replace(',', '.')
                                    const parsed = parseFloat(input)
                                    if (!isNaN(parsed) && parsed >= 0) {
                                      updateSubcontractorQuantity(item.articleId, sub.id, parsed)
                                    }
                                  }}
                                  className={`w-16 px-2 py-0.5 text-center text-sm rounded border ${
                                    isZeroQuantity
                                      ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                                      : hasCustomQuantity
                                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                                        : 'border-nature-200 dark:border-nature-600 bg-white dark:bg-nature-800'
                                  }`}
                                  title={isZeroQuantity ? 'Artikeln döljs för denna TG' : 'Antal för denna TG'}
                                />
                                <span className="text-nature-500 text-xs">st</span>
                                <span className="text-nature-600 dark:text-nature-400">
                                  {sub.name}
                                </span>
                                {isZeroQuantity && (
                                  <span className="text-xs text-orange-500">(dold)</span>
                                )}
                                {tgPrice !== undefined && !isZeroQuantity && (
                                  <span className="text-xs text-nature-400">
                                    ({tgPrice} kr × {displayQuantity} = {(tgPrice * displayQuantity).toLocaleString('sv-SE')} kr)
                                  </span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Total sum */}
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-nature-200 dark:border-nature-600">
                  <span className="font-semibold text-nature-900 dark:text-nature-100">Totalt (kundpris)</span>
                  <span className="font-semibold text-nature-900 dark:text-nature-100">
                    {plannedArticles.reduce((sum, item) => {
                      const price = customerPrices[item.articleId] ?? 0
                      return sum + (price * item.quantity)
                    }, 0).toLocaleString('sv-SE')} kr
                  </span>
                </div>
              </div>
            )}
                </div>
                )}
              </div>

            </div>
            )}

          </div>

        {/* KUNDDETALJER */}
        <CustomerCard
              customerId={formData.customer_id || null}
              customers={customers}
              assignmentId={id}
              assignmentNumber={assignmentNumber?.toString()}
              assignmentTitle={formData.title}
              assignmentDate={formData.start_date}
              assignmentAddress={getLocationDisplay(formData.location)}
              startTime={formData.start_time}
              commStatus={formData.customer_comm_status as CustomerCommStatus}
              invoiceStatus={formData.customer_invoice_status as CustomerInvoiceStatus}
              informedAt={formData.customer_informed_at}
              informedMethod={formData.customer_informed_method as ContactMethod | null}
              confirmedAt={formData.customer_confirmed_at}
              confirmedMethod={formData.customer_confirmed_method as ContactMethod | null}
              cancelledAt={formData.customer_cancelled_at}
              cancelledReason={formData.customer_cancelled_reason}
              invoiceNumber={formData.customer_invoice_number}
              invoiceDate={formData.customer_invoice_date}
              assignmentStatus={formData.status}
              isNewAssignment={isNewAssignment}
              otherAssignments={customerAssignments}
              tgList={subcontractorReports.map(r => {
                const sub = Array.isArray(r.subcontractors) ? r.subcontractors[0] : r.subcontractors
                return {
                  id: r.id,
                  subcontractorId: r.subcontractor_id,
                  name: sub?.name || 'Okänd',
                  phone: sub?.phone || null,
                  currentStatus: r.tg_status || 'preliminar_tilldelad'
                }
              })}
              onCustomerChange={(customerId) => setFormData(prev => ({ ...prev, customer_id: customerId }))}
              onCustomerClear={() => setFormData(prev => ({ ...prev, customer_id: '' }))}
              onCommStatusChange={handleCustomerCommStatusChange}
              onRegisterInvoice={handleRegisterCustomerInvoice}
              onCancellation={async (result: CancellationResult) => {
                if (!id || isNewAssignment) return
                const now = new Date().toISOString()

                // 1. Update assignment: set customer to avbokad, reset status to preliminär
                const { error } = await supabase
                  .from('assignments')
                  .update({
                    status: 'preliminar',
                    customer_comm_status: 'avbokad',
                    customer_comm_updated_at: now,
                    customer_cancelled_at: result.date,
                    customer_cancelled_reason: result.reason,
                    updated_at: now
                  })
                  .eq('id', id)

                if (error) {
                  console.error('Error cancelling assignment:', error)
                  toast.error('Kunde inte avboka uppdraget')
                  return
                }

                // 2. Reset all TG statuses to preliminar_tilldelad
                await supabase
                  .from('assignment_subcontractors')
                  .update({ tg_status: 'preliminar_tilldelad' })
                  .eq('assignment_id', id)

                // 3. Send SMS to selected TGs
                const tgListForSms = subcontractorReports
                  .filter(r => {
                    const sub = Array.isArray(r.subcontractors) ? r.subcontractors[0] : r.subcontractors
                    return result.sendSmsTo[r.id] && sub?.phone
                  })
                  .map(r => {
                    const sub = Array.isArray(r.subcontractors) ? r.subcontractors[0] : r.subcontractors
                    return {
                      id: r.id,
                      name: sub?.name || 'Okänd',
                      phone: sub?.phone || null,
                      subcontractorId: r.subcontractor_id
                    }
                  })

                let smsCount = 0
                const assignmentRef = assignmentNumber
                  ? `#${assignmentNumber} ${formData.title}`
                  : formData.title
                const dateTime = formData.start_time
                  ? `${formData.start_date} ${formData.start_time.substring(0, 5)}`
                  : formData.start_date

                for (const tg of tgListForSms) {
                  if (tg.phone) {
                    const smsMessage = `Uppdraget ${assignmentRef} (planerat ${dateTime}) har avbokats. ${result.reason}`
                    const smsResult = await sendSMS({
                      message: smsMessage,
                      recipient_type: 'subcontractor',
                      recipient_id: tg.subcontractorId,
                      recipient_name: tg.name,
                      recipient_phone: tg.phone,
                      assignment_id: id
                    })
                    if (smsResult.success) smsCount++
                  }
                }

                // 4. Update local state
                const stateUpdates = {
                  status: 'preliminar' as const,
                  customer_comm_status: 'avbokad' as CustomerCommStatus,
                  customer_comm_updated_at: now,
                  customer_cancelled_at: result.date,
                  customer_cancelled_reason: result.reason
                }
                setFormData(prev => ({ ...prev, ...stateUpdates }))
                setOriginalData(prev => prev ? { ...prev, ...stateUpdates } : prev)

                // 5. Log activity
                await logAssignmentChange(id, 'customer_cancelled', {
                  description: `Kund avbokade: ${result.reason}. Status återställd till Preliminär.${smsCount > 0 ? ` ${smsCount} SMS skickade.` : ''}`
                })

                // 6. Refresh data
                refetchReports()
                queryClient.invalidateQueries({ queryKey: ['assignment', id] })

                toast.success(`Avbokning registrerad${smsCount > 0 ? ` - ${smsCount} SMS skickade` : ''}`)
              }}
          isLocked={isLocked}
          validationError={!!validationErrors.customer_id}
          isCollapsed={customerCollapsed}
          onToggleCollapse={() => setCustomerCollapsed(!customerCollapsed)}
        />

        {/* TRÄDGÅRDSMÄSTARE */}
        <SubcontractorSection
            assignmentId={id || ''}
            isNewAssignment={isNewAssignment}
            isLocked={isLocked}
            assignmentStatus={formData.status}
            assignmentTitle={formData.title}
            assignmentNumber={assignmentNumber?.toString()}
            assignmentDate={formData.start_date}
            assignmentAddress={getLocationDisplay(formData.location)}
            customerInvoiceStatus={formData.customer_invoice_status}
            selectedSubcontractorIds={formData.subcontractor_ids}
            subcontractors={subcontractors}
            subcontractorReports={subcontractorReports.map((r: any) => ({
              ...r,
              // Ensure subcontractors is a single object (not array from Supabase join)
              subcontractors: Array.isArray(r.subcontractors) ? r.subcontractors[0] : r.subcontractors
            }))}
            plannedArticles={plannedArticles}
            articles={articles}
            onAddSubcontractor={addSubcontractor}
            onRemoveSubcontractor={removeSubcontractor}
            onStatusChange={(status) => {
              // Update both formData AND originalData since attestation saves directly to DB
              setFormData(prev => ({ ...prev, status }))
              setOriginalData(prev => prev ? { ...prev, status } : prev)
            }}
          refetchReports={refetchReports}
          isCollapsed={subcontractorCollapsed}
          onToggleCollapse={() => setSubcontractorCollapsed(!subcontractorCollapsed)}
        />

        {/* CENTRAL RAPPORTERING */}
        {!isNewAssignment && id && (
          <CentralReportSection
            assignmentId={id}
            initialArticles={adminReportedArticles}
            articles={articles}
            isCollapsed={centralReportCollapsed}
            onToggleCollapse={() => setCentralReportCollapsed(!centralReportCollapsed)}
          />
        )}

        {/* BILAGOR */}
        {!isNewAssignment && id && (
          <AttachmentSection assignmentId={id} canUpload={true} defaultCollapsed={true} />
        )}

        {/* KOMMENTARER */}
        {!isNewAssignment && id && (
          <CommentSection
            entityType="assignment"
            entityId={id}
            title="Kommentarer"
            defaultCollapsed={false}
            collapseIfEmpty={false}
          />
        )}

        {/* AKTIVITETSLOGG */}
        {!isNewAssignment && id && (
          <ActivityLog
            assignmentId={id}
            isCollapsed={activityLogCollapsed}
            onToggleCollapse={() => setActivityLogCollapsed(!activityLogCollapsed)}
          />
        )}
      </div>

      {/* Previous Assignments Modal */}
      {showPreviousAssignmentsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[80vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-nature-200 dark:border-nature-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1.5 sm:p-2 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                    <History className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-nature-900 dark:text-nature-100">
                    Tidigare uppdrag
                  </h3>
                </div>
                <button
                  onClick={() => setShowPreviousAssignmentsModal(false)}
                  className="p-1.5 sm:p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors bg-nature-100 dark:bg-nature-700 sm:bg-transparent"
                >
                  <X className="h-5 w-5 text-nature-500" />
                </button>
              </div>
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-2">
                Välj uppdrag att importera till beskrivningen
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 hidden sm:block">
                OBS: Artiklarna som visas är det som var planerat, inte vad som rapporterades.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-6">
              {previousAssignments.length === 0 ? (
                <p className="text-center text-nature-500 dark:text-nature-400 py-8">
                  Inga tidigare uppdrag hittades för denna kund
                </p>
              ) : (
                <div className="space-y-3">
                  {previousAssignments.map(assignment => {
                    const isSelected = selectedPreviousAssignments.has(assignment.id)
                    const idDisplay = assignment.assignment_number ? `#${assignment.assignment_number}` : assignment.id.slice(0, 8)
                    return (
                      <button
                        key={assignment.id}
                        onClick={() => togglePreviousAssignmentSelection(assignment.id)}
                        className={`w-full text-left p-4 border rounded-lg transition-colors ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-nature-200 dark:border-nature-600 hover:bg-nature-50 dark:hover:bg-nature-700'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-primary-500 border-primary-500'
                              : 'border-nature-300 dark:border-nature-500'
                          }`}>
                            {isSelected && (
                              <CheckCircle className="h-4 w-4 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-medium text-nature-900 dark:text-nature-100">
                                <span className="text-nature-500 dark:text-nature-400 mr-1">{idDisplay}</span>
                                {assignment.title}
                              </h4>
                              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  assignment.status === 'slutford' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                  assignment.status === 'attesterad' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  assignment.status === 'granskning' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                  assignment.status === 'pagaende' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                  'bg-nature-100 text-nature-600 dark:bg-nature-700 dark:text-nature-400'
                                }`}>
                                  {assignment.status === 'slutford' ? 'Slutförd' :
                                   assignment.status === 'attesterad' ? 'Attesterad' :
                                   assignment.status === 'granskning' ? 'Granskning' :
                                   assignment.status === 'pagaende' ? 'Pågående' :
                                   assignment.status}
                                </span>
                                <span className="text-xs text-nature-500 dark:text-nature-400">
                                  {assignment.start_date || 'Inget datum'}
                                </span>
                              </div>
                            </div>
                            {assignment.subcontractors.length > 0 && (
                              <p className="text-xs text-nature-500 dark:text-nature-400 mb-1">
                                TG: {assignment.subcontractors.map(s => s.phone ? `${s.name} (${s.phone})` : s.name).join(', ')}
                              </p>
                            )}
                            {assignment.articles.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-1">
                                {assignment.articles.slice(0, 4).map((a, i) => (
                                  <span
                                    key={i}
                                    className="text-xs bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400 px-2 py-0.5 rounded"
                                  >
                                    {a.quantity} {a.unit || 'st'} {a.name}
                                  </span>
                                ))}
                                {assignment.articles.length > 4 && (
                                  <span className="text-xs text-nature-500 dark:text-nature-400">
                                    +{assignment.articles.length - 4} till
                                  </span>
                                )}
                              </div>
                            )}
                            {assignment.description && (
                              <p className="text-sm text-nature-600 dark:text-nature-400 line-clamp-2">
                                {getCleanDescription(assignment.description)}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            {previousAssignments.length > 0 && (
              <div className="p-4 border-t border-nature-200 dark:border-nature-700 flex items-center justify-between">
                <span className="text-sm text-nature-500 dark:text-nature-400">
                  {selectedPreviousAssignments.size} uppdrag valda
                </span>
                <button
                  onClick={importSelectedPreviousAssignments}
                  disabled={selectedPreviousAssignments.size === 0}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-nature-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <History className="h-4 w-4" />
                  Importera valda
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Ta bort uppdrag
              </h3>
            </div>
            <p className="text-nature-600 dark:text-nature-400 mb-6">
              Är du säker på att du vill ta bort uppdraget <strong>"{formData.title}"</strong>?
              Denna åtgärd kan inte ångras.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-nature-700 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>{deleting ? 'Tar bort...' : 'Ta bort'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Past Date Confirmation Modal */}
      {showPastDateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <Calendar className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Datum har passerat
              </h3>
            </div>
            <p className="text-nature-600 dark:text-nature-400 mb-6">
              Du har valt ett datum som redan har passerat ({formData.start_date}).
              Är du säker på att du vill skapa uppdraget med detta datum?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPastDateConfirm(false)}
                className="px-4 py-2 text-nature-700 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  setPastDateConfirmed(true)
                  setShowPastDateConfirm(false)
                  // Trigger save again after confirmation
                  setTimeout(() => handleSave(), 0)
                }}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Calendar className="h-4 w-4" />
                <span>Ja, skapa ändå</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Change Rebooking Modal */}
      <DateChangeRebookingModal
        isOpen={showRebookingModal}
        onClose={() => {
          setShowRebookingModal(false)
          setPendingDateChange(null)
          setPendingTimeChange(null)
          setIsTimeChangeOnly(false)
        }}
        originalDate={originalData?.start_date || ''}
        newDate={pendingDateChange || ''}
        startTime={formData.start_time}
        // Time change props
        originalStartTime={originalData?.start_time}
        originalEndTime={originalData?.end_time}
        newStartTime={pendingTimeChange?.newStartTime}
        newEndTime={pendingTimeChange?.newEndTime}
        isTimeChangeOnly={isTimeChangeOnly}
        assignmentNumber={assignmentNumber ? String(assignmentNumber) : undefined}
        assignmentTitle={formData.title}
        customerName={selectedCustomer?.full_name || 'Kund'}
        customerCurrentStatus={formData.customer_comm_status as CustomerCommStatus}
        currentAssignmentStatus={formData.status}
        tgList={subcontractorReports
          .filter(report => report.tg_status !== 'avsagt')
          .map(report => ({
            id: report.id,
            subcontractorId: report.subcontractor_id,
            name: (report.subcontractors as any)?.name || 'Okänd TG',
            phone: (report.subcontractors as any)?.phone || null,
            currentStatus: report.tg_status as TgWorkStatus
          }))}
        onConfirm={handleRebookingConfirm}
      />

      {/* Planning confirmation modal */}
      <ConfirmPlanningModal
        isOpen={showPlanningModal}
        onClose={() => setShowPlanningModal(false)}
        assignmentNumber={assignmentNumber ? String(assignmentNumber) : undefined}
        assignmentTitle={formData.title}
        assignmentDate={formData.start_date}
        assignmentAddress={getLocationDisplay(formData.location)}
        startTime={formData.start_time}
        customerName={selectedCustomer?.full_name || 'Kund'}
        customerConfirmed={formData.customer_comm_status === 'bekraftad'}
        tgList={subcontractorReports
          .filter(report => report.tg_status === 'preliminar_tilldelad')
          .map(report => {
            const sub = Array.isArray(report.subcontractors) ? report.subcontractors[0] : report.subcontractors
            return {
              id: report.id,
              subcontractorId: report.subcontractor_id,
              name: sub?.name || 'Okänd TG',
              phone: sub?.phone || null
            }
          })}
        onConfirm={handlePlanningConfirmed}
      />

      {/* Add subcontractor modal (for confirmed/in-progress assignments) */}
      <AddSubcontractorModal
        isOpen={showAddSubcontractorModal}
        onClose={() => {
          setShowAddSubcontractorModal(false)
          setPendingSubcontractor(null)
        }}
        subcontractorName={pendingSubcontractor?.name || ''}
        subcontractorPhone={pendingSubcontractor?.phone || null}
        assignmentNumber={assignmentNumber || undefined}
        assignmentTitle={formData.title}
        assignmentDate={formData.start_date}
        assignmentTime={formData.start_time}
        currentAssignmentStatus={formData.status}
        onConfirm={handleAddSubcontractorConfirm}
      />

      {/* TG Quantity Warning Modal */}
      {showTgQuantityWarning && tgQuantityWarningData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {tgQuantityWarningData.type === 'add' ? 'TG tillagd med 0 antal' : 'Ta bort TG?'}
              </h3>
            </div>
            <p className="text-nature-600 dark:text-nature-400 mb-6">
              {tgQuantityWarningData.type === 'add' ? (
                <>
                  <strong>{tgQuantityWarningData.subName}</strong> har lagts till på uppdraget med <strong>0 st</strong> på alla artiklar.
                  <br /><br />
                  Justera artikelfördelningen manuellt om denna TG ska ha artiklar tilldelade.
                </>
              ) : (
                <>
                  Vill du ta bort <strong>{tgQuantityWarningData.subName}</strong> från uppdraget?
                  <br /><br />
                  Artikelfördelningen kommer behöva justeras manuellt för kvarvarande TG.
                </>
              )}
            </p>
            <div className="flex justify-end gap-3">
              {tgQuantityWarningData.type === 'remove' && (
                <button
                  onClick={() => {
                    setShowTgQuantityWarning(false)
                    setTgQuantityWarningData(null)
                  }}
                  className="px-4 py-2 text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
                >
                  Avbryt
                </button>
              )}
              <button
                onClick={() => {
                  if (tgQuantityWarningData.type === 'remove') {
                    doRemoveSubcontractor(tgQuantityWarningData.subId)
                  }
                  setShowTgQuantityWarning(false)
                  setTgQuantityWarningData(null)
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  tgQuantityWarningData.type === 'add'
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {tgQuantityWarningData.type === 'add' ? 'OK' : 'Ta bort'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Slide Panel */}
      <CalendarSlidePanel
        isOpen={showCalendarPanel}
        onClose={() => setShowCalendarPanel(false)}
        assignmentId={id || ''}
        assignmentStatus={formData.status}
        currentDate={formData.start_date}
        onDateChange={(date, requiresConfirmation) => {
          if (requiresConfirmation) {
            handleDateChange(date)
          } else {
            setFormData(prev => ({ ...prev, start_date: date }))
          }
          setShowCalendarPanel(false)
        }}
        assignedSubcontractorIds={subcontractorReports.map((r: any) => r.subcontractor_id)}
        onAddSubcontractor={(subcontractor: { id: string; name: string; phone: string | null }) => {
          // Close panel first, then open add modal with the selected TG
          setShowCalendarPanel(false)
          setPendingSubcontractor(subcontractor)
          setShowAddSubcontractorModal(true)
        }}
        assignmentTitle={formData.title}
      />
    </div>
  )
}

